import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { 
  Plus, Search, FileText, MoreHorizontal, Eye, EyeOff, Edit, Trash2, Download, 
  Send, CheckCircle2, XCircle, Clock, Filter, Calendar, CloudUpload, ExternalLink, Mail,
  Sparkles, Zap, Package, ChevronUp, ChevronDown, Undo2, Loader2, Copy, Paperclip, Layers, PenLine
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PdfViewer from '../components/PdfViewer';
import ProjectPhaseBadge from '../components/ProjectPhaseBadge';
import EmailComposer from '../components/EmailComposer';
import EmailPreview, { EmailPreviewButton } from '../components/EmailPreview';
import OfferteDocumenten from '../components/OfferteDocumenten';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import SearchSelect from '../components/SearchSelect';
import api, { offerteAPI, customerAPI, propertyAPI, companySettingsAPI, installationAPI, installationOfferteAPI, installationProductAPI, installationTemplateAPI, installationFactuurAPI, offerteTemplateAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { usePrivacy } from '../context/PrivacyContext';
import { toast } from 'sonner';

const getCustomerFileName = (c) => {
  const company = (c?.company_name || '').trim();
  if (company && company.toLowerCase() !== 'onbekend') return company;
  return `${c?.first_name || ''} ${c?.last_name || ''}`.trim();
};

const Offertes = () => {
  const [activeTab, setActiveTab] = useState('installatie');
  const [offertes, setOffertes] = useState([]);
  const [installationOffertes, setInstallationOffertes] = useState([]);
  const [phaseMap, setPhaseMap] = useState({});
  useEffect(() => {
    api.get('/installation-offertes/project-phases').then(r => setPhaseMap(r.data || {})).catch(() => {});
  }, []);
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [installations, setInstallations] = useState({});
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [creatorFilter, setCreatorFilter] = useState('all');
  const [phaseFilter, setPhaseFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [installationDialogOpen, setInstallationDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingOfferte, setEditingOfferte] = useState(null);
  const [editingInstallationOfferte, setEditingInstallationOfferte] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Post-creation popup state
  const [postCreateDialogOpen, setPostCreateDialogOpen] = useState(false);
  const [createdOfferteId, setCreatedOfferteId] = useState(null);
  const [createdOfferteData, setCreatedOfferteData] = useState(null);
  const [postCreateOptions, setPostCreateOptions] = useState({
    downloadPdf: false,
    sendEmail: false
  });
  
  // PDF Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewOfferte, setPreviewOfferte] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // Product Selector Dialog state
  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);  // For multi-select
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');  // Category filter
  const [previewLoading, setPreviewLoading] = useState(false);
  const [driveUrl, setDriveUrl] = useState(null);
  const [driveSaving, setDriveSaving] = useState(false);
  
  // Installation PDF Preview state
  const [installationPreviewOpen, setInstallationPreviewOpen] = useState(false);
  const [installationPreviewOfferte, setInstallationPreviewOfferte] = useState(null);
  const [installationPreviewUrl, setInstallationPreviewUrl] = useState(null);
  const [installationPreviewLoading, setInstallationPreviewLoading] = useState(false);
  
  // Email Composer state
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const [emailComposerData, setEmailComposerData] = useState(null);
  const [emailComposerType, setEmailComposerType] = useState('reiniging_offerte');
  const [emailPdfBase64, setEmailPdfBase64] = useState(null);
  const [emailPdfName, setEmailPdfName] = useState(null);
  
  // Email Preview state
  const [emailPreviewData, setEmailPreviewData] = useState({});
  const [emailPreviewLoading, setEmailPreviewLoading] = useState({});
  
  // Offerte documenten (foto's, legplan, datasheets) dialog state
  const [docsDialog, setDocsDialog] = useState({ open: false, offerte: null, entityType: 'offerte' });
  const [spProjectDialog, setSpProjectDialog] = useState(null);
  const [emailSending, setEmailSending] = useState({});
  
  // Offerte Templates state
  const [offerteTemplates, setOfferteTemplates] = useState([]);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateType, setTemplateType] = useState('reiniging'); // 'reiniging' or 'installatie'
  
  const { hasRole } = useAuth();
  const { privacy } = usePrivacy();
  const money = (v) => privacy ? '€ \u2022\u2022\u2022\u2022' : `\u20ac${(Number(v) || 0).toFixed(2)}`;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const defaultItem = { description: '', quantity: 1, unit_price: 0 };
  
  const [formData, setFormData] = useState({
    customer_id: '',
    property_id: '',
    offerte_number: '', // Optional manual override
    title: 'Onderhoudscontract reiniging PV panelen',
    items: [
      { description: 'Reinigen PV panelen', quantity: 0, unit_price: 12.50 },
      { description: 'Verplaatsingskosten forfait', quantity: 1, unit_price: 30.49 },
    ],
    discount: 0,
    vat_percentage: 6,
    payment_terms: '100% 3-Jaarlijks te factureren bij aanvang van deze overeenkomst en voor een periode van 9 jaar (verlengbaar met 9 jaar)\nJaarlijks opzegbaar mits opzegging 3 maanden voor aanvangsdatum',
    validity_days: 8,
    notes: 'Wateraansluiting Gardena-koppeling noodzakelijk te voorzien door de klant.',
    contract_duration: '9 jaar',
    contract_type: '3_yearly',
    language: 'nl', // Language selection: nl, fr, en
  });

  // Installation offerte form state
  const [installationFormData, setInstallationFormData] = useState({
    customer_id: '',
    property_id: '',
    offerte_number: '', // Optional manual override
    title: '', // Title for the offerte section (bold, above products)
    language: 'nl', // Language selection: nl, fr, en
    items: [],
    vat_percentage: 6,
    validity_days: 8,
    payment_terms: '40% Voorschot\n50% Na montage van de installatie en inwerkingstelling\n10% Na AREI-keuring op conformiteit door een erkend organisme',
    included_in_offerte: 'Leveren en plaatsen van de installatie\nTransport en materiaalkosten\nAREI-keuring op conformiteit door een erkend organisme',
    notes: '',
    total_power_wp: null,
    total_capacity_kwh: null,
    hide_unit_prices: false,
    custom_payment_terms: null, // Custom percentages per offerte: [{percentage: 40}, {percentage: 50}, {percentage: 10}]
    use_manual_subtotal: false, // NEW: Enable manual subtotal override
    manual_subtotal: null, // NEW: Manual subtotal excl BTW
    installation_address: '', // Handmatig plaatsingsadres (leeg = zelfde als gekoppeld adres)
  });
  const [manualPlacement, setManualPlacement] = useState(false); // Handmatige invoer plaatsingsadres
  
  useEffect(() => {
    fetchData();
  }, []);
  
  // Handle URL params for pre-filled offerte creation (from PropertyDetail)
  useEffect(() => {
    const action = searchParams.get('action');
    const type = searchParams.get('type');
    const propertyId = searchParams.get('property_id');
    const customerId = searchParams.get('customer_id');
    const panelCount = parseInt(searchParams.get('panel_count') || '0', 10);
    
    if (action === 'create' && type === 'reiniging' && propertyId && !loading) {
      // Find property for title
      const property = properties.find(p => p.id === propertyId);
      const propertyAddress = property ? `${property.street} ${property.number}, ${property.city}` : '';
      
      // Pre-fill form with data from PropertyDetail
      setFormData(prev => ({
        ...prev,
        customer_id: customerId || '',
        property_id: propertyId,
        title: `Offerte reiniging - ${propertyAddress}`,
        items: [
          { 
            description: 'Reinigen PV panelen', 
            quantity: panelCount || 0, 
            unit_price: settings?.default_cleaning_price_per_panel || 12.50 
          },
          { 
            description: 'Verplaatsingskosten forfait', 
            quantity: 1, 
            unit_price: settings?.default_travel_costs || 30.49 
          },
        ],
      }));
      
      // Open the dialog
      setEditingOfferte(null);
      setDialogOpen(true);
      
      // Clear URL params to prevent re-opening on refresh
      setSearchParams({});
    }
  }, [searchParams, loading, properties, settings]);
  
  const fetchData = async () => {
    try {
      // Fetch all data with individual error handling to prevent one failure from breaking all
      const results = await Promise.allSettled([
        offerteAPI.getAll(),
        installationOfferteAPI.getAll(),
        customerAPI.getAll(),
        propertyAPI.getAll(),
        companySettingsAPI.get(),
        installationProductAPI.getAll(),
        installationTemplateAPI.getAll(),
        offerteTemplateAPI.getAll('reiniging'),
      ]);
      
      // Extract data from settled promises (handle both fulfilled and rejected)
      const [offertesRes, installationOffertesRes, customersRes, propertiesRes, settingsRes, productsRes, templatesRes, offerteTemplatesRes] = results;
      
      if (offertesRes.status === 'fulfilled') setOffertes(offertesRes.value.data || []);
      if (installationOffertesRes.status === 'fulfilled') setInstallationOffertes(installationOffertesRes.value.data || []);
      if (customersRes.status === 'fulfilled') setCustomers(customersRes.value.data || []);
      if (propertiesRes.status === 'fulfilled') setProperties(propertiesRes.value.data || []);
      if (settingsRes.status === 'fulfilled') setSettings(settingsRes.value.data);
      if (productsRes.status === 'fulfilled') setProducts(productsRes.value.data || []);
      if (templatesRes.status === 'fulfilled') setTemplates(templatesRes.value.data || []);
      if (offerteTemplatesRes.status === 'fulfilled') setOfferteTemplates(offerteTemplatesRes.value.data || []);
      
      // Log any failures for debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`API call ${index} failed:`, result.reason);
        }
      });
      
      // Update form with settings defaults
      if (settingsRes.status === 'fulfilled' && settingsRes.value.data) {
        const settingsData = settingsRes.value.data;
        setFormData(prev => ({
          ...prev,
          payment_terms: settingsData.default_payment_terms || prev.payment_terms,
          vat_percentage: settingsData.default_vat_percentage || prev.vat_percentage,
          validity_days: settingsData.default_validity_days || prev.validity_days,
          items: [
            { description: 'Reinigen PV panelen', quantity: 0, unit_price: settingsData.default_cleaning_price_per_panel || 12.50 },
            { description: 'Verplaatsingskosten forfait', quantity: 1, unit_price: settingsData.default_travel_costs || 30.49 },
          ]
        }));
      }
      
      // Fetch installations for panel count
      try {
        const installRes = await installationAPI.getAll();
        const installMap = {};
        (installRes.data || []).forEach(i => {
          if (!installMap[i.property_id]) installMap[i.property_id] = [];
          installMap[i.property_id].push(i);
        });
        setInstallations(installMap);
      } catch (installError) {
        console.error('Failed to fetch installations:', installError);
      }
      
      // Show error only if critical data failed (customers or properties)
      if (customersRes.status === 'rejected' || propertiesRes.status === 'rejected') {
        toast.error('Kon sommige data niet laden');
      }
    } catch (error) {
      toast.error('Kon data niet laden');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const getPanelCount = (propertyId) => {
    const propInstallations = installations[propertyId] || [];
    const solar = propInstallations.find(i => i.installation_type === 'solar_panels');
    // Check both field names for compatibility
    return solar?.number_of_panels || solar?.panel_count || 0;
  };
  
  const customerOptions = customers.map(c => ({
    value: c.id,
    label: c.company_name
      ? `${c.company_name} — ${c.first_name} ${c.last_name}`
      : `${c.first_name} ${c.last_name}`,
  }));
  
  const propertyOptions = useMemo(() => {
    if (!formData.customer_id) return [];
    return properties
      .filter(p => p.customer_id === formData.customer_id)
      .map(p => ({
        value: p.id,
        label: `${p.street} ${p.number}, ${p.postal_code} ${p.city}`,
        panelCount: getPanelCount(p.id),
      }));
  }, [formData.customer_id, properties, installations]);
  
  const filteredOffertes = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return offertes.filter(o => {
      const matchesSearch = !search || 
        o.offerte_number?.toLowerCase().includes(search.toLowerCase()) ||
        o.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        o.property_address?.toLowerCase().includes(search.toLowerCase());
      
      const matchesCreator = creatorFilter === 'all' || o.created_by_name === creatorFilter;
      
      // Check if expired based on expiry_date
      const expiryDate = o.expiry_date ? new Date(o.expiry_date) : null;
      const isExpired = expiryDate && expiryDate < today && o.status !== 'accepted' && o.status !== 'rejected' && o.status !== 'not_chosen' && o.status !== 'invoiced';
      
      let matchesStatus;
      if (statusFilter === 'expired') {
        // Show offertes that are expired (by date) OR have status 'expired'
        matchesStatus = isExpired || o.status === 'expired';
      } else if (statusFilter === 'all') {
        matchesStatus = true;
      } else if (statusFilter === 'accepted') {
        // Geaccepteerd omvat ook reeds gefactureerde (invoiced) offertes
        matchesStatus = o.status === 'accepted' || o.status === 'invoiced';
      } else {
        matchesStatus = o.status === statusFilter;
      }
      
      return matchesSearch && matchesStatus && matchesCreator;
    });
  }, [offertes, search, statusFilter, creatorFilter]);

  const filteredInstallationOffertes = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return installationOffertes.filter(o => {
      const validityDate = o.validity_date ? new Date(o.validity_date) : null;
      const isExpiredByDate = validityDate && validityDate < today && o.status !== 'accepted' && o.status !== 'rejected' && o.status !== 'not_chosen';

      const matchesSearch = !search ||
        o.offerte_number?.toLowerCase().includes(search.toLowerCase()) ||
        o.customer_name?.toLowerCase().includes(search.toLowerCase());

      const matchesCreator = creatorFilter === 'all' || o.created_by_name === creatorFilter;

      let matchesStatus;
      if (statusFilter === 'expired') {
        matchesStatus = isExpiredByDate || o.status === 'expired';
      } else if (statusFilter === 'all') {
        matchesStatus = true;
      } else {
        matchesStatus = o.status === statusFilter;
      }

      let matchesPhase = true;
      if (phaseFilter !== 'all') {
        const info = phaseMap[o.id];
        const accepted = ['accepted', 'invoiced', 'geaccepteerd'].includes(o.status) || o.signed_at;
        const stage = info?.stage || (accepted ? 'te_plannen' : null);
        matchesPhase = stage === phaseFilter;
      }
      return matchesSearch && matchesStatus && matchesCreator && matchesPhase;
    });
  }, [installationOffertes, search, statusFilter, creatorFilter, phaseFilter, phaseMap]);

  const filteredTotal = useMemo(() =>
    filteredOffertes.reduce((sum, o) => sum + (o.total_incl_vat || 0), 0),
  [filteredOffertes]);

  const filteredInstallationTotal = useMemo(() =>
    filteredInstallationOffertes.reduce((sum, o) => sum + (o.total_incl_vat || 0), 0),
  [filteredInstallationOffertes]);

  const statusFilterLabel = {
    all: 'Alle statussen', draft: 'Concept', sent: 'Verzonden',
    accepted: 'Geaccepteerd', rejected: 'Afgewezen', expired: 'Verlopen', not_chosen: 'Niet gekozen versies'
  }[statusFilter] || statusFilter;

  const creatorOptions = useMemo(() => {
    const names = new Set();
    offertes.forEach(o => { if (o.created_by_name) names.add(o.created_by_name); });
    installationOffertes.forEach(o => { if (o.created_by_name) names.add(o.created_by_name); });
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [offertes, installationOffertes]);

  const creatorFilteredOffertes = useMemo(() =>
    creatorFilter === 'all' ? offertes : offertes.filter(o => o.created_by_name === creatorFilter),
  [offertes, creatorFilter]);

  const creatorFilteredInstallationOffertes = useMemo(() =>
    creatorFilter === 'all' ? installationOffertes : installationOffertes.filter(o => o.created_by_name === creatorFilter),
  [installationOffertes, creatorFilter]);
  
  const handlePropertyChange = (propertyId) => {
    setFormData(prev => ({ ...prev, property_id: propertyId }));
    
    // Auto-fill panel count
    const panelCount = getPanelCount(propertyId);
    if (panelCount > 0) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.map((item, idx) => 
          idx === 0 ? { ...item, quantity: panelCount } : item
        )
      }));
    }
  };
  
  const handleItemChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: field === 'description' ? value : parseFloat(value) || 0 } : item
      )
    }));
  };
  
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...defaultItem }]
    }));
  };
  
  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Move item up/down in cleaning form
  const moveItem = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.items.length) return;
    
    setFormData(prev => {
      const newItems = [...prev.items];
      [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
      return { ...prev, items: newItems };
    });
  };
  
  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const totalExclVat = subtotal - formData.discount;
    const vatAmount = totalExclVat * (formData.vat_percentage / 100);
    const totalInclVat = totalExclVat + vatAmount;
    return { subtotal, totalExclVat, vatAmount, totalInclVat };
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Selecteer een klant');
      return;
    }
    
    setSaving(true);
    try {
      if (editingOfferte) {
        await offerteAPI.update(editingOfferte.id, formData);
        toast.success('Offerte bijgewerkt');
        setDialogOpen(false);
        setEditingOfferte(null);
        fetchData();
        resetForm();
      } else {
        // Create new offerte
        const response = await offerteAPI.create(formData);
        toast.success('Offerte aangemaakt');
        setDialogOpen(false);
        
        // Store created offerte ID and data for post-create dialog
        if (response.data?.id) {
          setCreatedOfferteId(response.data.id);
          setCreatedOfferteData({
            ...formData,
            id: response.data.id,
            offerte_number: response.data.offerte_number
          });
          setPostCreateOptions({ downloadPdf: false, sendEmail: false });
          setPostCreateDialogOpen(true);
        }
        
        setEditingOfferte(null);
        fetchData();
        resetForm();
      }
    } catch (error) {
      toast.error('Kon offerte niet opslaan');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };
  
  const resetForm = () => {
    setFormData({
      customer_id: '',
      property_id: '',
      title: 'Onderhoudscontract reiniging PV panelen',
      items: [
        { description: 'Reinigen PV panelen', quantity: 0, unit_price: settings?.default_cleaning_price_per_panel || 12.50 },
        { description: 'Verplaatsingskosten forfait', quantity: 1, unit_price: settings?.default_travel_costs || 30.49 },
      ],
      discount: 0,
      vat_percentage: settings?.default_vat_percentage || 6,
      payment_terms: '100% 3-Jaarlijks te factureren bij aanvang van deze overeenkomst en voor een periode van 9 jaar (verlengbaar met 9 jaar)\nJaarlijks opzegbaar mits opzegging 3 maanden voor aanvangsdatum',
      validity_days: settings?.default_validity_days || 8,
      notes: 'Wateraansluiting Gardena-koppeling noodzakelijk te voorzien door de klant.',
      contract_duration: '9 jaar',
      contract_type: '3_yearly',
    });
  };

  const resetInstallationForm = () => {
    setInstallationFormData({
      customer_id: '',
      property_id: '',
      offerte_number: '',
      title: 'Offerte zonne-installatie',
      language: 'nl',
      items: [],
      vat_percentage: 6,
      validity_days: 8,
      payment_terms: '40% Voorschot\n50% Na montage van de installatie en inwerkingstelling\n10% Na AREI-keuring op conformiteit door een erkend organisme',
      included_in_offerte: 'Leveren en plaatsen van de installatie\nTransport en materiaalkosten\nAREI-keuring op conformiteit door een erkend organisme',
      notes: '',
      total_power_wp: null,
      total_capacity_kwh: null,
      hide_unit_prices: false,
      custom_payment_terms: null,
      use_manual_subtotal: false,
      manual_subtotal: null,
      installation_address: '',
    });
    setManualPlacement(false);
  };

  // Adresopties van de klant voor het plaatsingsadres
  const getPlacementOptions = (customerId) => 
    getCustomerProperties(customerId).map(p => `${p.street} ${p.number}, ${p.postal_code} ${p.city}`);

  // Add product to installation form
  const addInstallationItem = (product = null) => {
    const newItem = product ? {
      product_id: product.id,
      name: product.name,
      description: product.description || '',
      components: product.components || [],  // Include product components (spec lines)
      sub_products: (product.sub_products || []).map(sp => ({
        ...sp,
        quantity: sp.default_quantity || 1  // Use default_quantity as initial quantity
      })),
      quantity: 1,
      unit_price: product.unit_price,
      unit: product.unit,
      is_optional: false,
      show_price: false,  // Default: hide price on PDF
    } : {
      product_id: null,
      name: '',
      description: '',
      components: [],
      sub_products: [],
      quantity: 1,
      unit_price: 0,
      unit: 'stuk',
      is_optional: false,
      show_price: false,  // Default: hide price on PDF
    };
    
    setInstallationFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
  };

  // Remove item from installation form
  const removeInstallationItem = (index) => {
    setInstallationFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  // Move item up/down in installation form
  const moveInstallationItem = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= installationFormData.items.length) return;
    
    setInstallationFormData(prev => {
      const newItems = [...prev.items];
      [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
      return { ...prev, items: newItems };
    });
  };

  // Update item in installation form
  const updateInstallationItem = (index, field, value) => {
    setInstallationFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  // Wattpiek (Wp) uit een string halen, bv. "465 Wp" of "Zonnepaneel DMEGC 465Wp"
  const extractWp = (...sources) => {
    for (const s of sources) {
      const m = typeof s === 'string' && s.match(/(\d{3,4})\s*Wp/i);
      if (m) return parseInt(m[1], 10);
    }
    return null;
  };

  // Totaal wattpiek automatisch berekenen op basis van zonnepanelen-producten en aantal
  const computeTotalPanelWp = (items) => {
    let total = 0;
    for (const item of items) {
      const product = products.find(p => p.id === item.product_id);
      const isPanel = product ? product.category === 'zonnepanelen' : /zonnepan/i.test(item.name || '');
      if (isPanel) {
        const wp = extractWp(product?.specifications, product?.name, item.name);
        if (wp) total += wp * (parseFloat(item.quantity) || 0);
      }
      for (const sp of (item.sub_products || [])) {
        const spProduct = products.find(p => p.id === sp.product_id);
        const spIsPanel = spProduct ? spProduct.category === 'zonnepanelen' : /zonnepan/i.test(sp.name || '');
        if (spIsPanel) {
          const wp = extractWp(spProduct?.specifications, spProduct?.name, sp.name);
          if (wp) total += wp * (parseFloat(sp.quantity) || 0);
        }
      }
    }
    return total;
  };

  // Automatisch total_power_wp bijwerken wanneer items (panelen/aantallen) wijzigen
  useEffect(() => {
    if (!installationDialogOpen) return;
    const wp = computeTotalPanelWp(installationFormData.items);
    if (wp > 0 && wp !== installationFormData.total_power_wp) {
      setInstallationFormData(prev => ({ ...prev, total_power_wp: wp }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installationDialogOpen, JSON.stringify(installationFormData.items.map(i => ({ p: i.product_id, n: i.name, q: i.quantity, s: (i.sub_products || []).map(sp => ({ p: sp.product_id, n: sp.name, q: sp.quantity })) })))]);

  // Calculate installation totals
  const calculateInstallationTotals = () => {
    // Check if manual subtotal is enabled
    if (installationFormData.use_manual_subtotal && installationFormData.manual_subtotal !== null) {
      const subtotal = parseFloat(installationFormData.manual_subtotal) || 0;
      const vatAmount = subtotal * (installationFormData.vat_percentage / 100);
      const totalInclVat = subtotal + vatAmount;
      return { subtotal, vatAmount, totalInclVat, isManual: true };
    }
    
    // Calculate from items including sub-products
    const subtotal = installationFormData.items
      .filter(item => !item.is_optional)
      .reduce((sum, item) => {
        // Main item price
        const itemTotal = item.quantity * item.unit_price;
        
        // Sub-products price
        const subProductsTotal = (item.sub_products || []).reduce((spSum, sp) => {
          return spSum + ((sp.quantity || 0) * (sp.unit_price || 0));
        }, 0);
        
        return sum + itemTotal + subProductsTotal;
      }, 0);
    const vatAmount = subtotal * (installationFormData.vat_percentage / 100);
    const totalInclVat = subtotal + vatAmount;
    return { subtotal, vatAmount, totalInclVat, isManual: false };
  };

  // Save installation offerte
  const handleSaveInstallationOfferte = async () => {
    if (!installationFormData.customer_id || !installationFormData.property_id) {
      toast.error('Selecteer een klant en adres');
      return;
    }
    if (installationFormData.items.length === 0) {
      toast.error('Voeg minimaal één item toe');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...installationFormData,
        items: installationFormData.items.map(item => ({
          product_id: item.product_id,
          name: item.name,
          description: item.description,
          components: item.components || [],
          sub_products: (item.sub_products || []).map(sp => ({
            product_id: sp.product_id,
            name: sp.name,
            unit_price: sp.unit_price,
            unit: sp.unit,
            quantity: sp.quantity || 0
          })),
          quantity: item.quantity === '' || item.quantity === null || item.quantity === undefined ? 1 : parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price) || 0,
          unit: item.unit,
          is_optional: item.is_optional || false,
          show_price: item.show_price === true,  // Default: hide price, only show if explicitly true
        })),
      };
      
      if (editingInstallationOfferte) {
        await installationOfferteAPI.update(editingInstallationOfferte.id, data);
        toast.success('Offerte bijgewerkt');
      } else {
        await installationOfferteAPI.create(data);
        toast.success('Offerte aangemaakt');
      }
      
      setInstallationDialogOpen(false);
      resetInstallationForm();
      fetchData();
    } catch (error) {
      console.error('Error saving installation offerte:', error);
      toast.error('Kon offerte niet opslaan');
    } finally {
      setSaving(false);
    }
  };

  // Save installation offerte as template - open dialog
  const handleSaveAsInstallationTemplate = () => {
    if (installationFormData.items.length === 0) {
      toast.error('Voeg minimaal één item toe om een template op te slaan');
      return;
    }
    setTemplateType('installatie');
    setTemplateName(installationFormData.title || '');
    setSaveTemplateDialogOpen(true);
  };

  // Get properties for customer
  const getCustomerProperties = (customerId) => {
    return properties.filter(p => p.customer_id === customerId);
  };

  // Group products by category for dropdown
  const productsByCategory = [
    { value: 'zonnepanelen', label: 'Zonnepanelen' },
    { value: 'omvormers', label: 'Omvormers' },
    { value: 'batterijen', label: 'Batterijen' },
    { value: 'laadpalen', label: 'Laadpalen' },
    { value: 'montagemateriaal', label: 'Montagemateriaal' },
    { value: 'werken', label: 'Werken' },
    { value: 'elektriciteitswerken', label: 'Elektriciteitswerken' },
  ].map(cat => ({
    ...cat,
    products: products.filter(p => p.category === cat.value),
  }));

  // Load template into installation form
  const loadInstallationTemplate = (template) => {
    setInstallationFormData(prev => ({
      ...prev,
      items: template.items.map(item => ({
        ...item,
        total_price: item.quantity * item.unit_price,
      })),
      vat_percentage: template.vat_percentage,
      payment_terms: template.payment_terms,
      included_in_offerte: template.included_in_offerte,
      notes: template.notes || '',
    }));
    toast.success(`Template "${template.name}" geladen`);
  };
  
  // Handle post-create actions (download PDF, send email)
  const handlePostCreateConfirm = async () => {
    const shouldDownload = postCreateOptions.downloadPdf;
    const shouldSendEmail = postCreateOptions.sendEmail;
    
    setPostCreateDialogOpen(false);
    
    if (!createdOfferteId) return;
    
    // Download PDF directly if selected
    if (shouldDownload) {
      try {
        toast.loading('PDF downloaden...', { id: 'pdf-download' });
        
        const token = localStorage.getItem('token');
        const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/offertes/${createdOfferteId}/pdf`;
        
        const response = await fetch(downloadUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) throw new Error('Download failed');
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // Filename format: Solar Engineering_[nummer]_[bedrijf of klantnaam].pdf
        const createdCust = customers.find(c => c.id === createdOfferteData?.customer_id);
        const customerName = createdCust ? getCustomerFileName(createdCust) : (createdOfferteData?.customer_name || '');
        const offerteNumber = (createdOfferteData?.offerte_number || createdOfferteId).replace(/\//g, '-');
        link.setAttribute('download', `Solar Engineering_${offerteNumber}_${customerName}.pdf`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
        
        toast.success('PDF gedownload', { id: 'pdf-download' });
        fetchData();
      } catch (error) {
        toast.error('Kon PDF niet downloaden', { id: 'pdf-download' });
        console.error('PDF download error:', error);
      }
    }
    
    // Send email webhook if selected
    if (shouldSendEmail) {
      try {
        toast.loading('Email klaarzetten...', { id: 'email-send' });
        
        // Use stored offerte data from creation
        const customer = customers.find(c => c.id === createdOfferteData?.customer_id);
        const property = properties.find(p => p.id === createdOfferteData?.property_id);
        
        const customerName = customer ? `${customer.first_name} ${customer.last_name}` : 'Onbekend';
        const customerEmail = customer?.email || '';
        const propertyAddress = property ? `${property.street} ${property.number}, ${property.postal_code} ${property.city}` : '';
        const panelCount = getPanelCount(createdOfferteData?.property_id);
        
        const webhookData = {
          offerte_id: createdOfferteId,
          customer_name: customerName,
          customer_email: customerEmail,
          property_address: propertyAddress,
          panel_count: panelCount
        };
        
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/offerte-reiniging-mail`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(webhookData)
        });
        
        toast.success('Email klaargezet in Mails > Klaargezet', { id: 'email-send' });
      } catch (error) {
        toast.error('Kon email niet klaarzetten', { id: 'email-send' });
        console.error(error);
      }
    }
    
    setCreatedOfferteId(null);
    setCreatedOfferteData(null);
  };
  
  const handlePostCreateSkip = () => {
    setPostCreateDialogOpen(false);
    setCreatedOfferteId(null);
  };
  
  const handleEdit = (offerte) => {
    setEditingOfferte(offerte);
    setFormData({
      customer_id: offerte.customer_id,
      property_id: offerte.property_id || '',
      offerte_number: offerte.offerte_number || '',
      title: offerte.title,
      items: offerte.items.map(i => ({
        description: i.description,
        quantity: i.quantity,
        unit_price: i.unit_price,
      })),
      discount: offerte.discount,
      vat_percentage: offerte.vat_percentage,
      payment_terms: offerte.payment_terms,
      validity_days: offerte.validity_days,
      notes: offerte.notes || '',
      contract_duration: offerte.contract_duration,
      contract_type: offerte.contract_type,
      language: offerte.language || 'nl',
    });
    setDialogOpen(true);
  };
  
  const handleDelete = async (id) => {
    try {
      await offerteAPI.delete(id);
      toast.success('Offerte verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Kon offerte niet verwijderen');
    }
    setDeleteConfirm(null);
  };

  // Duplicate installation offerte
  const handleDuplicateInstallation = (offerte) => {
    // Pre-fill form with existing offerte data (except id and offerte_number)
    setInstallationFormData({
      customer_id: offerte.customer_id,
      property_id: offerte.property_id,
      offerte_date: new Date().toISOString().split('T')[0], // Today's date
      title: offerte.title || 'Offerte zonne-installatie',
      language: offerte.language || 'nl',
      items: offerte.items?.map(item => ({
        ...item,
        components: item.components?.map(c => ({...c})) || [],
        sub_products: item.sub_products?.map(sp => ({...sp})) || [],
      })) || [],
      vat_percentage: offerte.vat_percentage || 6,
      payment_terms: offerte.payment_terms || '',
      valid_days: offerte.valid_days || 14,
      validity_days: offerte.validity_days || offerte.valid_days || 14,
      included_in_offerte: offerte.included_in_offerte || '',
      conditions: offerte.conditions || '',
      notes: offerte.notes || '',
      custom_fields: offerte.custom_fields?.map(cf => ({...cf})) || [],
      show_product_prices: offerte.show_product_prices ?? false,
      hide_unit_prices: offerte.hide_unit_prices || false,
      total_power_wp: offerte.total_power_wp || 0,
      total_capacity_kwh: offerte.total_capacity_kwh || 0,
      installation_address: offerte.installation_address || '',
    });
    setManualPlacement(!!offerte.installation_address && !getPlacementOptions(offerte.customer_id).includes(offerte.installation_address));
    
    setEditingInstallationOfferte(null); // This is a NEW offerte, not editing
    setInstallationDialogOpen(true);
    toast.info('Offerte gedupliceerd - pas aan en sla op als nieuwe offerte');
  };

  const handleStatusChange = async (id, status) => {
    try {
      await offerteAPI.updateStatus(id, status);
      toast.success('Status bijgewerkt');
      fetchData();
    } catch (error) {
      toast.error('Kon status niet bijwerken');
    }
  };
  
  // Open email composer for reiniging offerte
  const handleSendOfferteEmail = async (offerte) => {
    // Find customer and property
    const customer = customers.find(c => c.id === offerte.customer_id);
    const property = properties.find(p => p.id === offerte.property_id);
    
    if (!customer?.email) {
      toast.error('Klant heeft geen email adres');
      return;
    }
    
    // Get PDF as base64
    toast.loading('Email voorbereiden...', { id: 'email-prep' });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/offertes/${offerte.id}/pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Could not load PDF');
      
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        
        setEmailPdfBase64(base64);
        // Filename format: Solar Engineering_[nummer]_[klantnaam].pdf
        const customerName = getCustomerFileName(customer);
        const offerteNumber = offerte.offerte_number.replace(/\//g, '-');
        setEmailPdfName(`Solar Engineering_${offerteNumber}_${customerName}.pdf`);
        setEmailComposerType('reiniging_offerte');
        setEmailComposerData({
          customer_id: customer.id,
          customer_name: `${customer.first_name} ${customer.last_name}`.trim(),
          customer_email: customer.email,
          address: property ? `${property.street} ${property.number}, ${property.postal_code} ${property.city}` : '',
          offerte_nummer: offerte.offerte_number,
          reference_number: offerte.offerte_number,
          reference_id: offerte.id,
          total_amount: offerte.total_incl_vat,
          panel_count: offerte.items?.find(i => i.description?.toLowerCase().includes('panelen'))?.quantity || '',
          validity_days: offerte.validity_days || 14,
          company_name: settings?.company_name || 'Business Assistant'
        });
        setEmailComposerOpen(true);
        toast.dismiss('email-prep');
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Error preparing email:', error);
      toast.error('Kon email niet voorbereiden', { id: 'email-prep' });
    }
  };
  
  // Open email composer for installation offerte
  const handleSendInstallationOfferteEmail = async (offerte) => {
    const customer = customers.find(c => c.id === offerte.customer_id);
    const property = properties.find(p => p.id === offerte.property_id);
    
    if (!customer?.email) {
      toast.error('Klant heeft geen email adres');
      return;
    }
    
    toast.loading('Email voorbereiden...', { id: 'email-prep' });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/installation-offertes/${offerte.id}/pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Could not load PDF');
      
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        
        setEmailPdfBase64(base64);
        // Filename format: Solar Engineering_[nummer]_[klantnaam].pdf
        const customerName = getCustomerFileName(customer);
        const offerteNumber = offerte.offerte_number.replace(/\//g, '-');
        setEmailPdfName(`Solar Engineering_${offerteNumber}_${customerName}.pdf`);
        setEmailComposerType('installatie_offerte');
        setEmailComposerData({
          customer_id: customer.id,
          customer_name: `${customer.first_name} ${customer.last_name}`.trim(),
          customer_email: customer.email,
          address: property ? `${property.street} ${property.number}, ${property.postal_code} ${property.city}` : '',
          offerte_nummer: offerte.offerte_number,
          reference_number: offerte.offerte_number,
          reference_id: offerte.id,
          total_amount: offerte.total_incl_vat,
          validity_days: offerte.validity_days || 14,
          company_name: settings?.company_name || 'Business Assistant',
          products: offerte.items?.map(item => ({
            name: item.name || item.description,
            quantity: item.quantity,
            brand: products.find(p => p.id === item.product_id)?.brand
          })) || []
        });
        setEmailComposerOpen(true);
        toast.dismiss('email-prep');
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Error preparing email:', error);
      toast.error('Kon email niet voorbereiden', { id: 'email-prep' });
    }
  };
  
  // Fetch email preview for hover tooltip
  const fetchEmailPreview = async (offerte, templateType) => {
    const key = `${offerte.id}-${templateType}`;
    
    // Return cached preview if available
    if (emailPreviewData[key]) {
      return emailPreviewData[key];
    }
    
    setEmailPreviewLoading(prev => ({ ...prev, [key]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/email-preview/offerte`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` 
          },
          body: JSON.stringify({
            offerte_id: offerte.id,
            template_type: templateType
          })
        }
      );
      
      if (!response.ok) throw new Error('Could not load preview');
      
      const data = await response.json();
      setEmailPreviewData(prev => ({ ...prev, [key]: data }));
      return data;
    } catch (error) {
      console.error('Error fetching email preview:', error);
      return null;
    } finally {
      setEmailPreviewLoading(prev => ({ ...prev, [key]: false }));
    }
  };
  
  // Queue email for later sending (Klaarzetten)
  const handleQueueEmail = async (offerte, templateType, editedBodyHtml = null, attachmentDocIds = []) => {
    const key = offerte.id;
    setEmailSending(prev => ({ ...prev, [key]: true }));
    
    try {
      const customer = customers.find(c => c.id === offerte.customer_id);
      const property = properties.find(p => p.id === offerte.property_id);
      
      if (!customer?.email) {
        toast.error('Klant heeft geen email adres');
        return;
      }
      
      // Haal de exacte preview op (zelfde als de popup toont) zodat de klaargezette
      // mail 100% identiek is aan de popup: zelfde titel, tekst en opmaak
      const preview = emailPreviewData[`${offerte.id}-${templateType}`] || await fetchEmailPreview(offerte, templateType);
      
      const webhookData = {
        offerte_id: offerte.id,
        customer_name: `${customer.first_name} ${customer.last_name}`.trim(),
        customer_email: customer.email,
        property_address: property ? `${property.street} ${property.number}, ${property.postal_code} ${property.city}` : '',
        panel_count: property?.number_of_panels || offerte.items?.find(i => i.description?.toLowerCase().includes('panelen'))?.quantity || 0,
        attachment_document_ids: attachmentDocIds || [],
        subject_override: preview?.subject || null,
        body_html_override: editedBodyHtml || preview?.body_html || null
      };
      
      const endpoint = templateType === 'reiniging_offerte' 
        ? '/api/webhook/offerte-reiniging-mail'
        : '/api/webhook/installatie-offerte-mail';
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(webhookData)
      });
      
      if (!response.ok) throw new Error('Failed to queue email');
      
      toast.success('Email klaargezet in Mails > Klaargezet');
    } catch (error) {
      console.error('Error queueing email:', error);
      toast.error('Kon email niet klaarzetten');
    } finally {
      setEmailSending(prev => ({ ...prev, [key]: false }));
    }
  };
  
  // Send email directly (Verzenden)
  const handleSendEmailDirect = async (offerte, templateType, editedBodyHtml = null, attachmentDocIds = []) => {
    const key = offerte.id;
    setEmailSending(prev => ({ ...prev, [key]: true }));
    
    try {
      const customer = customers.find(c => c.id === offerte.customer_id);
      const property = properties.find(p => p.id === offerte.property_id);
      
      if (!customer?.email) {
        toast.error('Klant heeft geen email adres');
        return;
      }
      
      // Get the email preview data
      const preview = emailPreviewData[`${offerte.id}-${templateType}`] || await fetchEmailPreview(offerte, templateType);
      if (!preview) {
        toast.error('Kon email preview niet laden');
        return;
      }
      
      // Get PDF as base64
      const token = localStorage.getItem('token');
      const pdfEndpoint = templateType.includes('installation') || templateType.includes('installatie')
        ? `/api/installation-offertes/${offerte.id}/pdf`
        : `/api/offertes/${offerte.id}/pdf`;
        
      const pdfResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}${pdfEndpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!pdfResponse.ok) throw new Error('Could not load PDF');
      
      const pdfBlob = await pdfResponse.blob();
      const pdfBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(pdfBlob);
      });
      
      // Send email
      const sendResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-email`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          to_email: customer.email,
          to_name: `${customer.first_name} ${customer.last_name}`.trim(),
          subject: preview.subject,
          body_html: editedBodyHtml || preview.body_html,
          attachment_base64: pdfBase64,
          attachment_document_ids: attachmentDocIds || [],
          // Filename format: Solar Engineering_[nummer]_[klantnaam].pdf
          attachment_name: `Solar Engineering_${offerte.offerte_number.replace(/\//g, '-')}_${customer.first_name} ${customer.last_name}`.trim() + '.pdf'
        })
      });
      
      if (!sendResponse.ok) {
        const errorData = await sendResponse.json();
        throw new Error(errorData.detail || 'Failed to send email');
      }
      
      toast.success('Email succesvol verzonden!');
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.message || 'Kon email niet verzenden');
    } finally {
      setEmailSending(prev => ({ ...prev, [key]: false }));
    }
  };
  
  // Save current offerte as template
  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Vul een template naam in');
      return;
    }
    
    setSavingTemplate(true);
    try {
      if (templateType === 'installatie') {
        // Save installation template
        const templateData = {
          name: templateName,
          description: installationFormData.title || '',
          items: installationFormData.items.map(item => ({
            product_id: item.product_id,
            name: item.name,
            description: item.description,
            components: item.components || [],
            sub_products: item.sub_products || [],
            quantity: item.quantity === '' || item.quantity === null || item.quantity === undefined ? 1 : parseFloat(item.quantity),
            unit_price: parseFloat(item.unit_price) || 0,
            unit: item.unit || 'stuk',
            is_optional: item.is_optional || false,
            show_price: item.show_price === true,
          })),
          vat_percentage: installationFormData.vat_percentage,
          payment_terms: installationFormData.payment_terms,
          included_in_offerte: installationFormData.included_in_offerte,
          notes: installationFormData.notes,
        };
        
        await api.post('/installation-templates', templateData);
        toast.success(`Template "${templateName}" opgeslagen!`);
        fetchData();
      } else {
        // Save reiniging template
        const templateData = {
          name: templateName,
          template_type: 'reiniging',
          title: formData.title,
          items: formData.items.map(item => ({
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unit_price
          })),
          discount: formData.discount,
          vat_percentage: formData.vat_percentage,
          payment_terms: formData.payment_terms,
          validity_days: formData.validity_days,
          notes: formData.notes,
          contract_duration: formData.contract_duration,
          contract_type: formData.contract_type
        };
        
        await offerteTemplateAPI.create(templateData);
        toast.success('Template opgeslagen!');
        
        // Refresh templates
        const templatesRes = await offerteTemplateAPI.getAll('reiniging');
        setOfferteTemplates(templatesRes.data || []);
      }
      
      setSaveTemplateDialogOpen(false);
      setTemplateName('');
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.detail || 'Kon template niet opslaan');
    } finally {
      setSavingTemplate(false);
    }
  };
  
  // Load template into form
  const handleLoadTemplate = async (templateId) => {
    if (!templateId) return;
    
    try {
      const response = await offerteTemplateAPI.getOne(templateId);
      const template = response.data;
      
      setFormData(prev => ({
        ...prev,
        title: template.title || prev.title,
        items: template.items && template.items.length > 0 ? template.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price
        })) : prev.items,
        discount: template.discount ?? prev.discount,
        vat_percentage: template.vat_percentage ?? prev.vat_percentage,
        payment_terms: template.payment_terms || prev.payment_terms,
        validity_days: template.validity_days ?? prev.validity_days,
        notes: template.notes || prev.notes,
        contract_duration: template.contract_duration || prev.contract_duration,
        contract_type: template.contract_type || prev.contract_type
      }));
      
      toast.success(`Template "${template.name}" geladen`);
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Kon template niet laden');
    }
  };
  
  // Delete template
  const handleDeleteTemplate = async (templateId) => {
    try {
      await offerteTemplateAPI.delete(templateId);
      toast.success('Template verwijderd');
      
      // Refresh templates
      const templatesRes = await offerteTemplateAPI.getAll('reiniging');
      setOfferteTemplates(templatesRes.data || []);
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Kon template niet verwijderen');
    }
  };
  
  // Load installation template into form
  const handleLoadInstallationTemplate = async (templateId) => {
    if (!templateId) return;
    
    try {
      const template = templates.find(t => t.id === templateId);
      if (!template) {
        toast.error('Template niet gevonden');
        return;
      }
      
      setInstallationFormData(prev => ({
        ...prev,
        title: template.description || template.name || prev.title,
        items: template.items && template.items.length > 0 ? template.items.map(item => ({
          product_id: item.product_id || '',
          description: item.description || '',
          quantity: item.quantity || 1,
          unit_price: item.unit_price || 0,
          vat_percentage: item.vat_percentage ?? 21
        })) : prev.items,
        vat_percentage: template.vat_percentage ?? prev.vat_percentage,
        included_work: template.included_work || prev.included_work,
        terms_conditions: template.terms_conditions || prev.terms_conditions
      }));
      
      toast.success(`Template "${template.name}" geladen`);
    } catch (error) {
      console.error('Error loading installation template:', error);
      toast.error('Kon template niet laden');
    }
  };
  
  const handlePreview = async (offerte) => {
    setPreviewOfferte(offerte);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl(null);
    setDriveUrl(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/offertes/${offerte.id}/pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Could not load PDF');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      setPreviewUrl(blobUrl);
      setPreviewLoading(false);
    } catch (error) {
      toast.error('Kon PDF niet laden');
      console.error(error);
      setPreviewLoading(false);
    }
  };

  // Direct PDF download - simplest approach
  const handleDirectDownload = async (offerte) => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/offertes/${offerte.id}/pdf`;
    
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed');
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = blobUrl;
      // Filename format: Solar Engineering_[nummer]_[klantnaam].pdf
      const fileCust = customers.find(c => c.id === offerte.customer_id);
      const customerName = fileCust ? getCustomerFileName(fileCust) : (offerte.customer_name || '');
      const offerteNumber = (offerte.offerte_number || offerte.id).replace(/\//g, '-');
      a.download = `Solar Engineering_${offerteNumber}_${customerName}.pdf`;
      a.click();
      
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(err);
      alert('Download mislukt. Probeer opnieuw.');
    }
  };

  // Direct PDF download for installation offertes
  const handleInstallationDirectDownload = async (offerte) => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/installation-offertes/${offerte.id}/pdf`;
    
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed');
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = blobUrl;
      // Filename format: Solar Engineering_[nummer]_[klantnaam].pdf
      const fileCust = customers.find(c => c.id === offerte.customer_id);
      const customerName = fileCust ? getCustomerFileName(fileCust) : (offerte.customer_name || '');
      const offerteNumber = (offerte.offerte_number || offerte.id).replace(/\//g, '-');
      a.download = `Solar Engineering_${offerteNumber}_${customerName}.pdf`;
      a.click();
      
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(err);
      alert('Download mislukt. Probeer opnieuw.');
    }
  };

  const handleDownloadFromPreview = async () => {
    if (!previewOfferte) return;
    
    try {
      toast.loading('Exporteren naar Google Drive...', { id: 'drive-export' });
      
      const response = await offerteAPI.exportToDrive(previewOfferte.id);
      
      if (response.data?.drive_url) {
        window.open(response.data.drive_url, '_blank');
        toast.success('Geopend in Google Drive', { id: 'drive-export' });
      } else if (response.data?.file_id) {
        window.open(`https://drive.google.com/file/d/${response.data.file_id}/view`, '_blank');
        toast.success('Geopend in Google Drive', { id: 'drive-export' });
      } else {
        toast.error('Kon niet openen', { id: 'drive-export' });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Kon niet exporteren naar Google Drive', { id: 'drive-export' });
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewOpen(false);
    setPreviewUrl(null);
    setPreviewOfferte(null);
    setDriveUrl(null);
  };

  // Installation offerte preview functions
  const handleInstallationPreview = async (offerte) => {
    setInstallationPreviewOfferte(offerte);
    setInstallationPreviewOpen(true);
    setInstallationPreviewLoading(true);
    setInstallationPreviewUrl(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/installation-offertes/${offerte.id}/pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Could not load PDF');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      setInstallationPreviewUrl(blobUrl);
      setInstallationPreviewLoading(false);
    } catch (error) {
      toast.error('Kon PDF niet laden');
      console.error(error);
      setInstallationPreviewLoading(false);
    }
  };

  const handleInstallationDownload = async () => {
    if (!installationPreviewOfferte) return;
    
    try {
      toast.loading('Exporteren naar Google Drive...', { id: 'drive-export' });
      
      const response = await installationOfferteAPI.exportToDrive(installationPreviewOfferte.id);
      
      if (response.data?.drive_url) {
        // Create hidden link and click to bypass popup blocker
        const link = document.createElement('a');
        link.href = response.data.drive_url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Geopend in Google Drive', { id: 'drive-export' });
        fetchData(); // Refresh to get updated drive_url
      } else {
        toast.error('Kon niet openen', { id: 'drive-export' });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Kon niet exporteren naar Google Drive', { id: 'drive-export' });
    }
  };

  const closeInstallationPreview = () => {
    if (installationPreviewUrl) {
      window.URL.revokeObjectURL(installationPreviewUrl);
    }
    setInstallationPreviewOpen(false);
    setInstallationPreviewUrl(null);
    setInstallationPreviewOfferte(null);
  };

  const handleInstallationExportToDrive = async (offerte) => {
    try {
      toast.loading('Exporteren naar Google Drive...', { id: 'drive-export' });
      const response = await installationOfferteAPI.exportToDrive(offerte.id);
      toast.success('Offerte geëxporteerd naar Google Drive', { id: 'drive-export' });
      
      if (response.data?.drive_url) {
        const link = document.createElement('a');
        link.href = response.data.drive_url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (response.data?.file_id) {
        const directDownloadUrl = `https://drive.google.com/uc?export=download&id=${response.data.file_id}`;
        const link = document.createElement('a');
        link.href = directDownloadUrl;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      fetchData();
    } catch (error) {
      toast.dismiss('drive-export');
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Kon niet exporteren naar Google Drive');
      }
      console.error(error);
    }
  };
  
  const handleExportToDrive = async (offerte) => {
    try {
      toast.loading('Exporteren naar Google Drive...', { id: 'drive-export' });
      // First download PDF to generate it
      await offerteAPI.downloadPdf(offerte.id);
      // Then export to Drive
      const response = await offerteAPI.exportToDrive(offerte.id);
      toast.success('Offerte geëxporteerd naar Google Drive', { id: 'drive-export' });
      
      // Trigger direct download using hidden link
      if (response.data?.file_id) {
        const directDownloadUrl = `https://drive.google.com/uc?export=download&id=${response.data.file_id}`;
        const link = document.createElement('a');
        link.href = directDownloadUrl;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      fetchData(); // Refresh to get updated google_drive_url
    } catch (error) {
      toast.dismiss('drive-export');
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Kon niet exporteren naar Google Drive');
      }
      console.error(error);
    }
  };
  
  const doSendToSharepoint = async (offerte, type, project) => {
    try {
      toast.loading('Offerte naar Sharepoint sturen...', { id: 'send-sp' });
      const endpoint = type === 'installation'
        ? `/installation-offertes/${offerte.id}/send-to-sharepoint`
        : `/offertes/${offerte.id}/send-to-sharepoint`;
      const res = await api.post(endpoint, project ? { project } : {});
      const projSuffix = res.data.project && res.data.project > 1 ? `?project=${res.data.project}` : '';
      const shareUrl = `${window.location.origin}/share/${res.data.share_token}${projSuffix}`;
      try { await navigator.clipboard.writeText(shareUrl); } catch (e) {}
      toast.success(`Offerte staat klaar in Sharepoint (Project ${res.data.project || 1}) — deellink gekopieerd naar klembord`, { id: 'send-sp', duration: 6000 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij versturen naar Sharepoint', { id: 'send-sp' });
    }
  };

  const handleSendToSharepoint = async (offerte, type) => {
    try {
      const res = await api.get(`/sharepoint/customer/${offerte.customer_id}/projects`);
      const info = res.data;
      if (info.exists) {
        const alreadySent = (info.projects || []).some(p => (p.quote_ids || []).includes(offerte.id));
        const hasOtherQuotes = (info.projects || []).some(p => (p.quote_ids || []).some(q => q !== offerte.id));
        if (!alreadySent && hasOtherQuotes) {
          setSpProjectDialog({ offerte, type, projects: info.projects });
          return;
        }
      }
    } catch (e) { /* bij twijfel direct versturen */ }
    doSendToSharepoint(offerte, type);
  };

  const getSignedBadge = (offerte) => offerte.digitally_signed ? (
    <Badge variant="outline" className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30" data-testid={`signed-badge-${offerte.id}`}>
      <PenLine className="w-3 h-3 mr-1" />
      Ondertekend{offerte.signed_by_name ? ` · ${offerte.signed_by_name}` : ''}
    </Badge>
  ) : null;

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      sent: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      accepted: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      expired: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      not_chosen: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    };
    const labels = {
      draft: 'Concept',
      sent: 'Verzonden',
      accepted: 'Geaccepteerd',
      rejected: 'Afgewezen',
      expired: 'Verlopen',
      not_chosen: 'Niet gekozen versie',
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.draft}>
        {labels[status] || status}
      </Badge>
    );
  };
  
  const totals = calculateTotals();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="offertes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Offertes</h1>
          <p className="text-muted-foreground">Beheer offertes voor reinigingen en installaties</p>
        </div>
        {hasRole(['admin', 'sales']) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="create-offerte-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nieuwe Offerte
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                resetForm(); 
                setEditingOfferte(null); 
                setDialogOpen(true);
              }}>
                <Sparkles className="w-4 h-4 mr-2" />
                Reiniging Offerte
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                resetInstallationForm();
                setEditingInstallationOfferte(null);
                setInstallationDialogOpen(true);
              }}>
                <Zap className="w-4 h-4 mr-2" />
                Installatie Offerte
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Tabs for Reiniging / Installatie */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="installatie" className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Installatie ({installationOffertes.length})
          </TabsTrigger>
          <TabsTrigger value="reiniging" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Reiniging ({offertes.length})
          </TabsTrigger>
        </TabsList>

        {/* Reiniging Offertes Tab */}
        <TabsContent value="reiniging" className="mt-4 space-y-4">
      
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Zoeken op nummer, klant, adres..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            <SelectItem value="draft">Concept</SelectItem>
            <SelectItem value="sent">Verzonden</SelectItem>
            <SelectItem value="accepted">Geaccepteerd</SelectItem>
            <SelectItem value="rejected">Afgewezen</SelectItem>
            <SelectItem value="expired">Verlopen</SelectItem>
            <SelectItem value="not_chosen">Niet gekozen versie</SelectItem>
          </SelectContent>
        </Select>
        <Select value={creatorFilter} onValueChange={setCreatorFilter}>
          <SelectTrigger className="w-[200px]" data-testid="creator-filter-select">
            <SelectValue placeholder="Aanmaker" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle aanmakers</SelectItem>
            {creatorOptions.map(name => (
              <SelectItem key={name} value={name}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Total price of filtered offertes */}
      <Card className="border-primary/30 bg-primary/5" data-testid="reiniging-total-banner">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-sm text-muted-foreground">Totaalprijs — {statusFilterLabel}{creatorFilter !== 'all' ? ` · ${creatorFilter}` : ''} ({filteredOffertes.length} offertes)</p>
            <p className="text-2xl font-bold text-primary" data-testid="reiniging-total-amount">{money(filteredTotal)} <span className="text-sm font-normal text-muted-foreground">incl. BTW</span></p>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {['draft', 'sent', 'accepted', 'rejected', 'expired', 'not_chosen'].map(status => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          let count;
          if (status === 'expired') {
            // Count offertes that are expired by date (not accepted/rejected/invoiced) OR have status 'expired'
            count = creatorFilteredOffertes.filter(o => {
              const expiryDate = o.expiry_date ? new Date(o.expiry_date) : null;
              const isExpiredByDate = expiryDate && expiryDate < today && o.status !== 'accepted' && o.status !== 'rejected' && o.status !== 'not_chosen' && o.status !== 'invoiced';
              return isExpiredByDate || o.status === 'expired';
            }).length;
          } else if (status === 'accepted') {
            count = creatorFilteredOffertes.filter(o => o.status === 'accepted' || o.status === 'invoiced').length;
          } else {
            count = creatorFilteredOffertes.filter(o => o.status === status).length;
          }
          const labels = { draft: 'Concept', sent: 'Verzonden', accepted: 'Geaccepteerd', rejected: 'Afgewezen', expired: 'Verlopen', not_chosen: 'Niet gekozen versies' };
          const colors = { draft: 'text-gray-400', sent: 'text-blue-400', accepted: 'text-green-400', rejected: 'text-red-400', expired: 'text-orange-400', not_chosen: 'text-purple-400' };
          return (
            <Card key={status} className="cursor-pointer hover:bg-muted/50" onClick={() => setStatusFilter(status)}>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{labels[status]}</p>
                <p className={`text-2xl font-bold ${colors[status]}`}>{count}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Offertes List */}
      <div className="space-y-3">
        {filteredOffertes.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-lg font-medium text-muted-foreground">Geen offertes gevonden</p>
            <p className="text-sm text-muted-foreground mt-1">Maak een nieuwe offerte aan</p>
          </Card>
        ) : (
          filteredOffertes.map(offerte => (
            <Card key={offerte.id} className="hover:bg-muted/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 items-center justify-center flex-shrink-0 hidden sm:flex">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{offerte.offerte_number}</p>
                        {getStatusBadge(offerte.status)}
                        {getSignedBadge(offerte)}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{offerte.customer_name}</p>
                      {offerte.property_address && (
                        <p className="text-xs text-muted-foreground truncate">{offerte.property_address}</p>
                      )}
                      {offerte.created_by_name && (
                        <p className="text-xs text-muted-foreground mt-1">
                          <span className="text-muted-foreground/70">Aangemaakt door:</span> {offerte.created_by_name}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 sm:gap-6 flex-shrink-0">
                    <div className="text-right hidden md:block">
                      <p className="font-semibold">{money(offerte.total_incl_vat)}</p>
                      <p className="text-xs text-muted-foreground">incl. BTW</p>
                    </div>
                    <div className="text-right hidden md:block">
                      <p className="text-sm">{formatDate(offerte.issue_date)}</p>
                      <p className="text-xs text-muted-foreground">Geldig tot {formatDate(offerte.expiry_date)}</p>
                    </div>
                    
                    {/* Documenten (foto's, legplan, datasheets) */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDocsDialog({ open: true, offerte, entityType: 'offerte' })}
                      title="Documenten (foto's, legplan, datasheets)"
                      data-testid={`offerte-docs-btn-${offerte.id}`}
                    >
                      <Paperclip className="w-4 h-4" />
                    </Button>
                    
                    {/* Email Preview Button with Hover */}
                    <EmailPreviewButton
                      offerte={offerte}
                      templateType="reiniging_offerte"
                      onFetchPreview={fetchEmailPreview}
                      previewData={emailPreviewData[`${offerte.id}-reiniging_offerte`]}
                      loading={emailPreviewLoading[`${offerte.id}-reiniging_offerte`]}
                      onQueue={handleQueueEmail}
                      onSend={handleSendEmailDirect}
                      sending={emailSending[offerte.id]}
                      disabled={!customers.find(c => c.id === offerte.customer_id)?.email}
                    />
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`offerte-menu-${offerte.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => handlePreview(offerte)}>
                          <Eye className="w-4 h-4 mr-2" />
                          Bekijk PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => handleDirectDownload(offerte)}>
                          <Download className="w-4 h-4 mr-2" />
                          Exporteer als PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => handleSendOfferteEmail(offerte)} data-testid={`send-email-${offerte.id}`}>
                          <Mail className="w-4 h-4 mr-2" />
                          Email Versturen
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => handleSendToSharepoint(offerte, 'cleaning')} data-testid={`send-sharepoint-${offerte.id}`}>
                          <CloudUpload className="w-4 h-4 mr-2" />
                          Naar Sharepoint sturen
                        </DropdownMenuItem>
                        {hasRole(['admin', 'sales']) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onSelect={() => handleEdit(offerte)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Bewerken
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onSelect={() => handleStatusChange(offerte.id, 'sent')} disabled={offerte.status !== 'draft'}>
                              <Send className="w-4 h-4 mr-2" />
                              Markeer als verzonden
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => handleStatusChange(offerte.id, 'accepted')} className="text-green-500" disabled={offerte.status === 'accepted'}>
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Markeer als geaccepteerd
                            </DropdownMenuItem>
                            {offerte.status === 'accepted' && (
                              <DropdownMenuItem onSelect={() => handleStatusChange(offerte.id, 'sent')} className="text-orange-500">
                                <Undo2 className="w-4 h-4 mr-2" />
                                Terug naar verzonden
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onSelect={() => handleStatusChange(offerte.id, 'rejected')} className="text-red-500">
                              <XCircle className="w-4 h-4 mr-2" />
                              Markeer als afgewezen
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => handleStatusChange(offerte.id, 'not_chosen')} className="text-purple-500" data-testid="mark-not-chosen-reiniging">
                              <Layers className="w-4 h-4 mr-2" />
                              Markeer als 'Niet gekozen versie'
                            </DropdownMenuItem>
                          </>
                        )}
                        {hasRole('admin') && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onSelect={() => setDeleteConfirm(offerte.id)} className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Verwijderen
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
        </TabsContent>

        {/* Installatie Offertes Tab */}
        <TabsContent value="installatie" className="mt-4 space-y-4">
          {/* Search and Filter for Installation */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Zoeken op nummer, klant..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle statussen</SelectItem>
                <SelectItem value="draft">Concept</SelectItem>
                <SelectItem value="sent">Verzonden</SelectItem>
                <SelectItem value="accepted">Geaccepteerd</SelectItem>
                <SelectItem value="rejected">Afgewezen</SelectItem>
                <SelectItem value="expired">Verlopen</SelectItem>
                <SelectItem value="not_chosen">Niet gekozen versie</SelectItem>
              </SelectContent>
            </Select>
            <Select value={phaseFilter} onValueChange={setPhaseFilter}>
              <SelectTrigger className="w-[180px]" data-testid="phase-filter-select">
                <SelectValue placeholder="Projectfase" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle fases</SelectItem>
                <SelectItem value="te_plannen"><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500" />In te plannen</span></SelectItem>
                <SelectItem value="montage"><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-500" />Montage</span></SelectItem>
                <SelectItem value="omvormer"><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-yellow-400" />Omvormer</span></SelectItem>
                <SelectItem value="keuring"><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-purple-500" />Keuring</span></SelectItem>
                <SelectItem value="afgewerkt"><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500" />Afgewerkt</span></SelectItem>
              </SelectContent>
            </Select>
            <Select value={creatorFilter} onValueChange={setCreatorFilter}>
              <SelectTrigger className="w-[200px]" data-testid="creator-filter-select-install">
                <SelectValue placeholder="Aanmaker" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle aanmakers</SelectItem>
                {creatorOptions.map(name => (
                  <SelectItem key={name} value={name}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Total price of filtered installation offertes */}
          <Card className="border-amber-500/30 bg-amber-500/5" data-testid="installatie-total-banner">
            <CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-sm text-muted-foreground">Totaalprijs — {statusFilterLabel}{creatorFilter !== 'all' ? ` · ${creatorFilter}` : ''} ({filteredInstallationOffertes.length} offertes)</p>
                <p className="text-2xl font-bold text-amber-500" data-testid="installatie-total-amount">{money(filteredInstallationTotal)} <span className="text-sm font-normal text-muted-foreground">incl. BTW</span></p>
              </div>
            </CardContent>
          </Card>

          {/* Stats for Installation */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {['draft', 'sent', 'accepted', 'rejected', 'expired', 'not_chosen'].map(status => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              
              let count;
              if (status === 'expired') {
                count = creatorFilteredInstallationOffertes.filter(o => {
                  const validityDate = o.validity_date ? new Date(o.validity_date) : null;
                  const isExpiredByDate = validityDate && validityDate < today && o.status !== 'accepted' && o.status !== 'rejected' && o.status !== 'not_chosen';
                  return isExpiredByDate || o.status === 'expired';
                }).length;
              } else {
                count = creatorFilteredInstallationOffertes.filter(o => o.status === status).length;
              }
              const labels = { draft: 'Concept', sent: 'Verzonden', accepted: 'Geaccepteerd', rejected: 'Afgewezen', expired: 'Verlopen', not_chosen: 'Niet gekozen versies' };
              const colors = { draft: 'text-gray-400', sent: 'text-blue-400', accepted: 'text-green-400', rejected: 'text-red-400', expired: 'text-orange-400', not_chosen: 'text-purple-400' };
              return (
                <Card key={status} className="cursor-pointer hover:bg-muted/50" onClick={() => setStatusFilter(status)}>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">{labels[status]}</p>
                    <p className={`text-2xl font-bold ${colors[status]}`}>{count}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Installation Offertes List */}
          <div className="space-y-3">
            {filteredInstallationOffertes.length === 0 ? (
              <Card className="p-12 text-center">
                <Zap className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-lg font-medium text-muted-foreground">Geen installatie offertes gevonden</p>
                <p className="text-sm text-muted-foreground mt-1">Maak een nieuwe installatie offerte aan</p>
                <Button className="mt-4" onClick={() => { resetInstallationForm(); setEditingInstallationOfferte(null); setInstallationDialogOpen(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuwe Installatie Offerte
                </Button>
              </Card>
            ) : (
              filteredInstallationOffertes.map(offerte => (
                <Card key={offerte.id} className="hover:bg-muted/30 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/10 items-center justify-center flex-shrink-0 hidden sm:flex">
                          <Zap className="w-5 h-5 text-amber-500" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold">{offerte.offerte_number}</p>
                            {getStatusBadge(offerte.status)}
                            {getSignedBadge(offerte)}
                            <ProjectPhaseBadge offerte={offerte} phaseInfo={phaseMap[offerte.id]} />
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{offerte.customer_name}</p>
                          {offerte.property_address && (
                            <p className="text-xs text-muted-foreground truncate">{offerte.property_address}</p>
                          )}
                          {offerte.created_by_name && (
                            <p className="text-xs text-muted-foreground mt-1">
                              <span className="text-muted-foreground/70">Aangemaakt door:</span> {offerte.created_by_name}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1 sm:gap-6 flex-shrink-0">
                        <div className="text-right hidden md:block">
                          <p className="font-semibold">{money(offerte.total_incl_vat)}</p>
                          <p className="text-xs text-muted-foreground">incl. {offerte.vat_percentage}% BTW</p>
                        </div>
                        <div className="text-right hidden md:block">
                          <p className="text-sm">{formatDate(offerte.offerte_date)}</p>
                          <p className="text-xs text-muted-foreground">Geldig tot {formatDate(offerte.validity_date)}</p>
                        </div>
                        
                        {/* Documenten (foto's, legplan, datasheets) */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDocsDialog({ open: true, offerte, entityType: 'installation_offerte' })}
                          title="Documenten (foto's, legplan, datasheets)"
                          data-testid={`installation-offerte-docs-btn-${offerte.id}`}
                        >
                          <Paperclip className="w-4 h-4" />
                        </Button>
                        
                        {/* Email Preview Button with Hover */}
                        <EmailPreviewButton
                          offerte={offerte}
                          templateType="installatie_offerte"
                          onFetchPreview={fetchEmailPreview}
                          previewData={emailPreviewData[`${offerte.id}-installatie_offerte`]}
                          loading={emailPreviewLoading[`${offerte.id}-installatie_offerte`]}
                          onQueue={handleQueueEmail}
                          onSend={handleSendEmailDirect}
                          sending={emailSending[offerte.id]}
                          disabled={!customers.find(c => c.id === offerte.customer_id)?.email}
                        />
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`installation-offerte-menu-${offerte.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleInstallationPreview(offerte)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Bekijk PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleInstallationDirectDownload(offerte)}>
                              <Download className="w-4 h-4 mr-2" />
                              Exporteer als PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSendInstallationOfferteEmail(offerte)} data-testid={`send-installation-email-${offerte.id}`}>
                              <Mail className="w-4 h-4 mr-2" />
                              Email Versturen
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSendToSharepoint(offerte, 'installation')} data-testid={`send-sharepoint-installatie-${offerte.id}`}>
                              <CloudUpload className="w-4 h-4 mr-2" />
                              Naar Sharepoint sturen
                            </DropdownMenuItem>
                            {hasRole(['admin', 'sales']) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => {
                                  setEditingInstallationOfferte(offerte);
                                  setInstallationFormData({
                                    customer_id: offerte.customer_id,
                                    property_id: offerte.property_id,
                                    offerte_number: offerte.offerte_number || '',
                                    offerte_date: offerte.offerte_date || '',
                                    title: offerte.title,
                                    language: offerte.language || 'nl',
                                    items: offerte.items || [],
                                    vat_percentage: offerte.vat_percentage,
                                    validity_days: offerte.validity_days || 14,
                                    payment_terms: offerte.payment_terms,
                                    included_in_offerte: offerte.included_in_offerte || '',
                                    notes: offerte.notes || '',
                                    total_power_wp: offerte.total_power_wp,
                                    total_capacity_kwh: offerte.total_capacity_kwh,
                                    hide_unit_prices: offerte.hide_unit_prices || false,
                                    custom_payment_terms: offerte.custom_payment_terms || null,
                                    use_manual_subtotal: offerte.use_manual_subtotal || false,
                                    manual_subtotal: offerte.manual_subtotal || null,
                                    installation_address: offerte.installation_address || '',
                                  });
                                  setManualPlacement(!!offerte.installation_address && !getPlacementOptions(offerte.customer_id).includes(offerte.installation_address));
                                  setInstallationDialogOpen(true);
                                }}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Bewerken
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDuplicateInstallation(offerte)}>
                                  <Copy className="w-4 h-4 mr-2" />
                                  Dupliceren
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={async () => {
                                  await installationOfferteAPI.updateStatus(offerte.id, 'sent');
                                  toast.success('Status bijgewerkt naar Verzonden');
                                  fetchData();
                                }}>
                                  <Send className="w-4 h-4 mr-2" />
                                  Markeer als verzonden
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={async () => {
                                  await installationOfferteAPI.updateStatus(offerte.id, 'accepted');
                                  toast.success('Status bijgewerkt naar Geaccepteerd');
                                  fetchData();
                                }} className="text-green-500">
                                  <CheckCircle2 className="w-4 h-4 mr-2" />
                                  Markeer als geaccepteerd
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={async () => {
                                  await installationOfferteAPI.updateStatus(offerte.id, 'rejected');
                                  toast.success('Status bijgewerkt naar Afgewezen');
                                  fetchData();
                                }} className="text-red-500">
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Markeer als afgewezen
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={async () => {
                                  await installationOfferteAPI.updateStatus(offerte.id, 'not_chosen');
                                  toast.success("Gemarkeerd als 'Niet gekozen versie'");
                                  fetchData();
                                }} className="text-purple-500" data-testid="mark-not-chosen-installatie">
                                  <Layers className="w-4 h-4 mr-2" />
                                  Markeer als 'Niet gekozen versie'
                                </DropdownMenuItem>
                                
                                {/* Factuur per termijn aanmaken - alleen voor geaccepteerde offertes */}
                                {offerte.status === 'accepted' && (() => {
                                  // Get term percentages from offerte or use defaults
                                  const terms = offerte.custom_payment_terms || [
                                    { percentage: 40, description: 'Voorschot' },
                                    { percentage: 50, description: 'Na montage' },
                                    { percentage: 10, description: 'Eindfactuur' }
                                  ];
                                  return (
                                  <>
                                    <DropdownMenuSeparator />
                                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                      Factuur aanmaken
                                    </div>
                                    <DropdownMenuItem 
                                      onClick={async () => {
                                        try {
                                          toast.loading('Voorschotfactuur aanmaken...', { id: 'create-term' });
                                          await installationFactuurAPI.createTermFromOfferte(offerte.id, 1, terms[0]?.percentage);
                                          toast.success(`Voorschotfactuur (${terms[0]?.percentage}%) aangemaakt`, { id: 'create-term' });
                                          fetchData();
                                        } catch (error) {
                                          toast.error(error.response?.data?.detail || 'Fout bij aanmaken factuur', { id: 'create-term' });
                                        }
                                      }}
                                      disabled={offerte.invoiced_terms?.includes(1)}
                                      className={offerte.invoiced_terms?.includes(1) ? 'opacity-50' : 'text-blue-600'}
                                    >
                                      <FileText className="w-4 h-4 mr-2" />
                                      Termijn 1 - {terms[0]?.description} ({terms[0]?.percentage}%)
                                      {offerte.invoiced_terms?.includes(1) && ' ✓'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={async () => {
                                        try {
                                          toast.loading('Factuur na montage aanmaken...', { id: 'create-term' });
                                          await installationFactuurAPI.createTermFromOfferte(offerte.id, 2, terms[1]?.percentage);
                                          toast.success(`Factuur na montage (${terms[1]?.percentage}%) aangemaakt`, { id: 'create-term' });
                                          fetchData();
                                        } catch (error) {
                                          toast.error(error.response?.data?.detail || 'Fout bij aanmaken factuur', { id: 'create-term' });
                                        }
                                      }}
                                      disabled={offerte.invoiced_terms?.includes(2)}
                                      className={offerte.invoiced_terms?.includes(2) ? 'opacity-50' : 'text-blue-600'}
                                    >
                                      <FileText className="w-4 h-4 mr-2" />
                                      Termijn 2 - {terms[1]?.description} ({terms[1]?.percentage}%)
                                      {offerte.invoiced_terms?.includes(2) && ' ✓'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={async () => {
                                        try {
                                          toast.loading('Eindfactuur aanmaken...', { id: 'create-term' });
                                          await installationFactuurAPI.createTermFromOfferte(offerte.id, 3, terms[2]?.percentage);
                                          toast.success(`Eindfactuur (${terms[2]?.percentage}%) aangemaakt`, { id: 'create-term' });
                                          fetchData();
                                        } catch (error) {
                                          toast.error(error.response?.data?.detail || 'Fout bij aanmaken factuur', { id: 'create-term' });
                                        }
                                      }}
                                      disabled={offerte.invoiced_terms?.includes(3)}
                                      className={offerte.invoiced_terms?.includes(3) ? 'opacity-50' : 'text-blue-600'}
                                    >
                                      <FileText className="w-4 h-4 mr-2" />
                                      Termijn 3 - {terms[2]?.description} ({terms[2]?.percentage}%)
                                      {offerte.invoiced_terms?.includes(3) && ' ✓'}
                                    </DropdownMenuItem>
                                  </>
                                );})()}
                              </>
                            )}
                            {hasRole('admin') && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => setDeleteConfirm({ type: 'installation', ...offerte })}
                                  className="text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Verwijderen
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOfferte ? 'Offerte Bewerken' : 'Nieuwe Offerte'}</DialogTitle>
            <DialogDescription>
              {editingOfferte ? `Offerte ${editingOfferte.offerte_number} bewerken` : 'Maak een nieuwe offerte voor een reinigingscontract'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer & Property */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Klant *</Label>
                <SearchSelect
                  options={customerOptions}
                  value={formData.customer_id}
                  onChange={(value) => setFormData(prev => ({ ...prev, customer_id: value, property_id: '' }))}
                  placeholder="Selecteer klant..."
                  displayKey="label"
                  valueKey="value"
                />
              </div>
              <div className="space-y-2">
                <Label>Adres</Label>
                <SearchSelect
                  options={propertyOptions}
                  value={formData.property_id}
                  onChange={handlePropertyChange}
                  placeholder="Selecteer adres..."
                  displayKey="label"
                  valueKey="value"
                  disabled={!formData.customer_id}
                />
              </div>
            </div>
            
            {/* Title & Contract Type & Language */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Offertenummer</Label>
                <Input
                  value={formData.offerte_number}
                  onChange={(e) => {
                    // Replace / with - to prevent invalid format
                    const sanitized = e.target.value.replace(/\//g, '-');
                    setFormData(prev => ({ ...prev, offerte_number: sanitized }));
                  }}
                  placeholder="Automatisch"
                />
                <p className="text-xs text-muted-foreground">Laat leeg voor automatische nummering (gebruik - niet /)</p>
              </div>
              <div className="space-y-2">
                <Label>Titel offerte</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Contract type</Label>
                <Select 
                  value={formData.contract_type} 
                  onValueChange={(value) => {
                    // Automatically set contract_duration and payment_terms based on contract type
                    let duration, paymentTerms;
                    if (value === '1_malig') {
                      duration = 'Eenmalig';
                      paymentTerms = '100% te factureren voor de uitvoering van de reiniging';
                    } else if (value === '2_yearly') {
                      duration = '6 jaar';
                      paymentTerms = '100% 2-Jaarlijks te factureren bij aanvang van deze overeenkomst en voor een periode van 6 jaar (verlengbaar met 6 jaar)\nJaarlijks opzegbaar mits opzegging 3 maanden voor aanvangsdatum';
                    } else {
                      duration = '9 jaar';
                      paymentTerms = '100% 3-Jaarlijks te factureren bij aanvang van deze overeenkomst en voor een periode van 9 jaar (verlengbaar met 9 jaar)\nJaarlijks opzegbaar mits opzegging 3 maanden voor aanvangsdatum';
                    }
                    setFormData(prev => ({ 
                      ...prev, 
                      contract_type: value, 
                      contract_duration: duration,
                      payment_terms: paymentTerms
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1_malig">1-malig (eenmalige reiniging)</SelectItem>
                    <SelectItem value="2_yearly">2-jaarlijks (6 jaar, 3 reinigingen)</SelectItem>
                    <SelectItem value="3_yearly">3-jaarlijks (9 jaar, 3 reinigingen)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Taal offerte/email</Label>
                <Select 
                  value={formData.language || 'nl'} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, language: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nl">Nederlands</SelectItem>
                    <SelectItem value="fr">Frans</SelectItem>
                    <SelectItem value="en">Engels</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Regels</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Regel toevoegen
                </Button>
              </div>
              
              <div className="space-y-2">
                {formData.items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    {/* Move buttons */}
                    <div className="flex flex-col justify-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0"
                        onClick={() => moveItem(index, 'up')}
                        disabled={index === 0}
                      >
                        <ChevronUp className="h-3 w-3" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0"
                        onClick={() => moveItem(index, 'down')}
                        disabled={index === formData.items.length - 1}
                      >
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </div>
                    <Input
                      placeholder="Omschrijving"
                      value={item.description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Aantal"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      className="w-24"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Prijs"
                      value={item.unit_price}
                      onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                      className="w-28"
                    />
                    <div className="w-28 text-right py-2 text-sm">
                      €{(item.quantity * item.unit_price).toFixed(2)}
                    </div>
                    {formData.items.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(index)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Totals */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotaal excl. BTW</span>
                <span>€{totals.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <Label className="text-sm">Korting</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.discount}
                  onChange={(e) => setFormData(prev => ({ ...prev, discount: parseFloat(e.target.value) || 0 }))}
                  className="w-32 text-right"
                />
              </div>
              <div className="flex justify-between text-sm">
                <span>Totaal excl. BTW</span>
                <span>€{totals.totalExclVat.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <Label className="text-sm">BTW %</Label>
                <Select 
                  value={String(formData.vat_percentage)} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, vat_percentage: parseInt(v) }))}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0% (intracommunautair/vrijstelling/medecontractant)</SelectItem>
                    <SelectItem value="6">6% (woning ouder dan 10 jaar)</SelectItem>
                    <SelectItem value="21">21% (woning jonger dan 10 jaar)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-between text-sm">
                <span>BTW bedrag</span>
                <span>€{totals.vatAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Totaal incl. BTW</span>
                <span>€{totals.totalInclVat.toFixed(2)}</span>
              </div>
            </div>
            
            {/* Terms */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Geldigheid (dagen)</Label>
                <Input
                  type="number"
                  value={formData.validity_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, validity_days: parseInt(e.target.value) || 8 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Contractduur</Label>
                <Input
                  value={formData.contract_duration}
                  onChange={(e) => setFormData(prev => ({ ...prev, contract_duration: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Betalingsvoorwaarden</Label>
              <Textarea
                value={formData.payment_terms}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Opmerkingen</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuleren
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Opslaan...' : editingOfferte ? 'Bijwerken' : 'Aanmaken'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Offerte verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Deze actie kan niet ongedaan gemaakt worden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                if (deleteConfirm?.type === 'installation') {
                  await installationOfferteAPI.delete(deleteConfirm.id);
                  toast.success('Installatie offerte verwijderd');
                  setDeleteConfirm(null);
                  fetchData();
                } else {
                  handleDelete(deleteConfirm);
                }
              }} 
              className="bg-destructive text-destructive-foreground"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Post-Create Options Dialog */}
      <Dialog open={postCreateDialogOpen} onOpenChange={setPostCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Offerte aangemaakt!
            </DialogTitle>
            <DialogDescription>
              Wat wil je doen met deze offerte?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex items-center space-x-3">
              <Checkbox 
                id="downloadPdf"
                checked={postCreateOptions.downloadPdf}
                onCheckedChange={(checked) => setPostCreateOptions(prev => ({ ...prev, downloadPdf: checked }))}
              />
              <label 
                htmlFor="downloadPdf" 
                className="text-sm font-medium leading-none cursor-pointer flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </label>
            </div>
            <div className="flex items-center space-x-3">
              <Checkbox 
                id="sendEmail"
                checked={postCreateOptions.sendEmail}
                onCheckedChange={(checked) => setPostCreateOptions(prev => ({ ...prev, sendEmail: checked }))}
              />
              <label 
                htmlFor="sendEmail" 
                className="text-sm font-medium leading-none cursor-pointer flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Maak bevestigingsmail
              </label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handlePostCreateSkip}>
              Sluiten
            </Button>
            <Button 
              onClick={handlePostCreateConfirm}
              disabled={!postCreateOptions.downloadPdf && !postCreateOptions.sendEmail}
            >
              Uitvoeren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={closePreview}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-4 pb-2 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {previewOfferte?.offerte_number}
                </DialogTitle>
                <DialogDescription>
                  {previewOfferte?.customer_name}
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => previewOfferte && handleDirectDownload(previewOfferte)}
                  data-testid="preview-download-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden bg-muted/50">
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-muted-foreground">PDF laden...</p>
                </div>
              </div>
            ) : previewUrl ? (
              <PdfViewer pdfUrl={previewUrl} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">Kon PDF niet laden</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Installation Offerte Dialog */}
      <Dialog open={installationDialogOpen} onOpenChange={(open) => {
        setInstallationDialogOpen(open);
        if (!open) {
          setEditingInstallationOfferte(null);
          resetInstallationForm();
        }
      }}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-lg sm:text-xl">
              {editingInstallationOfferte ? 'Installatie Offerte Bewerken' : 'Nieuwe Installatie Offerte'}
            </DialogTitle>
            <DialogDescription className="text-sm">
              Maak een offerte voor zonnepanelen, batterijen, laadpalen of omvormers
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden space-y-4 sm:space-y-6 py-4">
            {/* Customer & Property */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Klant *</Label>
                <SearchSelect
                  options={customers.map(c => ({
                    value: c.id,
                    label: c.company_name
                      ? `${c.company_name} — ${c.first_name} ${c.last_name}`
                      : `${c.first_name} ${c.last_name}`,
                    email: c.email
                  }))}
                  value={installationFormData.customer_id}
                  onChange={(id) => setInstallationFormData(prev => ({ ...prev, customer_id: id, property_id: '' }))}
                  placeholder="Selecteer klant..."
                  displayKey="label"
                  valueKey="value"
                />
              </div>
              
              {(() => {
                const instCust = customers.find(c => c.id === installationFormData.customer_id);
                const isB2B = !!(instCust?.company_name?.trim() && instCust?.billing_street?.trim());
                const instProps = getCustomerProperties(installationFormData.customer_id);
                if (isB2B) {
                  return (
                    <>
                      <div className="space-y-2">
                        <Label>Adres (bedrijf)</Label>
                        <div className="flex items-center h-10 px-3 rounded-md border border-border/60 bg-muted/40 text-sm" data-testid="b2b-company-address">
                          {instCust.billing_street}, {instCust.billing_postal_code || ''} {instCust.billing_city || ''}
                        </div>
                        <p className="text-xs text-muted-foreground">Facturatieadres van het bedrijf — wordt bovenaan de PDF getoond</p>
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label>Plaatsingsadres *</Label>
                        <Select
                          value={manualPlacement ? '__custom__' : (installationFormData.property_id || '')}
                          onValueChange={(v) => {
                            if (v === '__custom__') {
                              setManualPlacement(true);
                              setInstallationFormData(prev => ({ ...prev, property_id: prev.property_id || instProps[0]?.id || '' }));
                            } else {
                              setManualPlacement(false);
                              setInstallationFormData(prev => ({ ...prev, property_id: v, installation_address: '' }));
                            }
                          }}
                        >
                          <SelectTrigger data-testid="b2b-placement-select">
                            <SelectValue placeholder="Selecteer plaatsingsadres..." />
                          </SelectTrigger>
                          <SelectContent>
                            {instProps.map(prop => (
                              <SelectItem key={prop.id} value={prop.id}>
                                {prop.street} {prop.number}, {prop.postal_code} {prop.city}
                              </SelectItem>
                            ))}
                            <SelectItem value="__custom__">Handmatig invoeren...</SelectItem>
                          </SelectContent>
                        </Select>
                        {manualPlacement && (
                          <Input
                            value={installationFormData.installation_address || ''}
                            onChange={(e) => setInstallationFormData(prev => ({ ...prev, installation_address: e.target.value }))}
                            placeholder="bijv. Nieuwstraat 12, 1000 Brussel"
                            data-testid="placement-address-input"
                          />
                        )}
                        <p className="text-xs text-muted-foreground">Alle adressen van dit bedrijf — wordt als "Installatie-adres" op de PDF getoond</p>
                      </div>
                    </>
                  );
                }
                return (
                  <>
                    <div className="space-y-2">
                      <Label>Adres *</Label>
                      <Select 
                        value={installationFormData.property_id} 
                        onValueChange={(id) => setInstallationFormData(prev => ({ ...prev, property_id: id }))}
                        disabled={!installationFormData.customer_id}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer adres..." />
                        </SelectTrigger>
                        <SelectContent>
                          {instProps.map(prop => (
                            <SelectItem key={prop.id} value={prop.id}>
                              {prop.street} {prop.number}, {prop.city}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Plaatsingsadres op offerte</Label>
                      <Select
                        value={manualPlacement ? '__custom__' : (installationFormData.installation_address || '__default__')}
                        onValueChange={(v) => {
                          if (v === '__default__') {
                            setManualPlacement(false);
                            setInstallationFormData(prev => ({ ...prev, installation_address: '' }));
                          } else if (v === '__custom__') {
                            setManualPlacement(true);
                          } else {
                            setManualPlacement(false);
                            setInstallationFormData(prev => ({ ...prev, installation_address: v }));
                          }
                        }}
                        disabled={!installationFormData.customer_id}
                      >
                        <SelectTrigger data-testid="placement-address-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__default__">Zelfde als geselecteerd adres (standaard)</SelectItem>
                          {getPlacementOptions(installationFormData.customer_id).map(addr => (
                            <SelectItem key={addr} value={addr}>{addr}</SelectItem>
                          ))}
                          <SelectItem value="__custom__">Handmatig invoeren...</SelectItem>
                        </SelectContent>
                      </Select>
                      {manualPlacement && (
                        <Input
                          value={installationFormData.installation_address || ''}
                          onChange={(e) => setInstallationFormData(prev => ({ ...prev, installation_address: e.target.value }))}
                          placeholder="bijv. Nieuwstraat 12, 1000 Brussel"
                          data-testid="placement-address-input"
                        />
                      )}
                      <p className="text-xs text-muted-foreground">Wordt als "Installatie-adres" op de PDF getoond — handig als het facturatieadres anders is dan het plaatsingsadres</p>
                    </div>
                  </>
                );
              })()}
            </div>
            
            {/* Offerte Number, Date, Title & Language - Stacked on mobile */}
            <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-4 sm:gap-4">
              <div className="space-y-2">
                <Label>Offertenummer</Label>
                <Input
                  value={installationFormData.offerte_number}
                  onChange={(e) => {
                    // Replace / with - to prevent invalid format
                    const sanitized = e.target.value.replace(/\//g, '-');
                    setInstallationFormData(prev => ({ ...prev, offerte_number: sanitized }));
                  }}
                  placeholder="Automatisch"
                />
                <p className="text-xs text-muted-foreground">Laat leeg voor automatische nummering (gebruik - niet /)</p>
              </div>
              <div className="space-y-2">
                <Label>Offertedatum</Label>
                <Input
                  type="date"
                  value={installationFormData.offerte_date || ''}
                  onChange={(e) => setInstallationFormData(prev => ({ ...prev, offerte_date: e.target.value }))}
                  data-testid="offerte-date-input"
                />
                <p className="text-xs text-muted-foreground">Aanmaakdatum op de offerte (PDF)</p>
              </div>
              <div className="space-y-2">
                <Label>Titel</Label>
                <Input
                  value={installationFormData.title}
                  onChange={(e) => setInstallationFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Offerte zonne-installatie"
                />
              </div>
              <div className="space-y-2">
                <Label>Taal</Label>
                <Select 
                  value={installationFormData.language || 'nl'} 
                  onValueChange={(v) => setInstallationFormData(prev => ({ ...prev, language: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nl">Nederlands</SelectItem>
                    <SelectItem value="fr">Frans</SelectItem>
                    <SelectItem value="en">Engels</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Load Template - Stacked on mobile */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <Label className="whitespace-nowrap">Template laden:</Label>
                <Select onValueChange={(id) => {
                  const template = templates.find(t => t.id === id);
                  if (template) loadInstallationTemplate(template);
                }}>
                  <SelectTrigger className="w-full sm:w-64">
                    <SelectValue placeholder="Kies een template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                onClick={handleSaveAsInstallationTemplate}
                className="whitespace-nowrap bg-orange-600 hover:bg-orange-700 text-white w-full sm:w-auto"
              >
                <FileText className="w-4 h-4 mr-2" />
                Opslaan als template
              </Button>
            </div>
            
            {/* Items */}
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <Label className="text-base font-semibold">Items</Label>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 sm:flex-none"
                    onClick={() => {
                      setProductSearchQuery('');
                      setProductSelectorOpen(true);
                    }}
                  >
                    <Package className="h-4 w-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Product toevoegen</span>
                    <span className="sm:hidden">Product</span>
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={() => addInstallationItem()}>
                    <Plus className="h-4 w-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Aangepast item</span>
                    <span className="sm:hidden">Item</span>
                  </Button>
                </div>
              </div>
              
              {/* Items header - Hidden on mobile */}
              <div className="hidden sm:grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-2">
                <div className="col-span-4">Omschrijving</div>
                <div className="col-span-2">Aantal</div>
                <div className="col-span-2">Eenheidsprijs</div>
                <div className="col-span-2 text-right">Totaal</div>
                <div className="col-span-2 text-right">Acties</div>
              </div>
              
              {/* Items list - Card style on mobile, grid on desktop */}
              <div className="space-y-3">
                {installationFormData.items.map((item, index) => (
                  <div key={index} className="p-3 bg-muted/30 rounded-lg space-y-2">
                    {/* Mobile Layout */}
                    <div className="sm:hidden space-y-3">
                      <div className="flex justify-between items-start gap-2">
                        <Input
                          value={item.name}
                          onChange={(e) => updateInstallationItem(index, 'name', e.target.value)}
                          placeholder="Omschrijving"
                          className="text-sm font-medium flex-1"
                        />
                        <div className="flex items-center gap-1">
                          <div className="flex flex-col">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0"
                              onClick={() => moveInstallationItem(index, 'up')}
                              disabled={index === 0}
                            >
                              <ChevronUp className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0"
                              onClick={() => moveInstallationItem(index, 'down')}
                              disabled={index === installationFormData.items.length - 1}
                            >
                              <ChevronDown className="h-3 w-3" />
                            </Button>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => removeInstallationItem(index)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs text-muted-foreground">Aantal</label>
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateInstallationItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                            min="0"
                            step="1"
                            className="text-sm"
                            data-testid={`installation-item-qty-${index}`}
                          />
                          <span className="text-xs text-muted-foreground">{item.unit}</span>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">Prijs</label>
                          <Input
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => updateInstallationItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            min="0"
                            step="0.01"
                            className="text-sm"
                          />
                        </div>
                        <div className="flex flex-col justify-center items-end">
                          <label className="text-xs text-muted-foreground">Totaal</label>
                          <span className="font-medium text-sm">€{(item.quantity * item.unit_price).toFixed(2)}</span>
                        </div>
                      </div>
                      {/* Extra options for mobile */}
                      <div className="flex items-center justify-between pt-2 border-t border-muted">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={item.is_optional}
                            onCheckedChange={(checked) => updateInstallationItem(index, 'is_optional', checked)}
                          />
                          <span className="text-xs text-muted-foreground">Optioneel</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-7 px-2 ${item.show_price === true ? 'text-primary' : 'text-muted-foreground'}`}
                          onClick={() => updateInstallationItem(index, 'show_price', !item.show_price)}
                        >
                          {item.show_price === true ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
                          <span className="text-xs">{item.show_price === true ? 'Prijs zichtbaar' : 'Prijs verborgen'}</span>
                        </Button>
                      </div>
                    </div>
                    
                    {/* Desktop Layout */}
                    <div className="hidden sm:grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-4">
                        <Input
                          value={item.name}
                          onChange={(e) => updateInstallationItem(index, 'name', e.target.value)}
                          placeholder="Omschrijving"
                          className="text-sm font-medium"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateInstallationItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="1"
                          className="text-sm"
                          data-testid={`installation-item-qty-compact-${index}`}
                        />
                        <span className="text-xs text-muted-foreground">{item.unit}</span>
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => updateInstallationItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          className="text-sm"
                        />
                      </div>
                      <div className="col-span-2 text-right">
                        <span className="font-medium">€{(item.quantity * item.unit_price).toFixed(2)}</span>
                      </div>
                      <div className="col-span-2 flex items-center gap-1 justify-end">
                        {/* Move buttons */}
                        <div className="flex flex-col">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0"
                            onClick={() => moveInstallationItem(index, 'up')}
                            disabled={index === 0}
                          >
                            <ChevronUp className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0"
                            onClick={() => moveInstallationItem(index, 'down')}
                            disabled={index === installationFormData.items.length - 1}
                          >
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-7 w-7 p-0 ${item.show_price === true ? 'text-primary' : 'text-muted-foreground'}`}
                          onClick={() => updateInstallationItem(index, 'show_price', !item.show_price)}
                          title={item.show_price === true ? 'Prijs zichtbaar op PDF' : 'Prijs verborgen op PDF'}
                        >
                          {item.show_price === true ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </Button>
                        <div className="flex items-center gap-1">
                          <Checkbox
                            checked={item.is_optional}
                            onCheckedChange={(checked) => updateInstallationItem(index, 'is_optional', checked)}
                          />
                          <span className="text-xs text-muted-foreground">Opt.</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeInstallationItem(index)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Components / Sub-lines section with editable quantities */}
                    {item.components && item.components.length > 0 && (
                      <div className="pl-4 border-l-2 border-primary/20 space-y-1">
                        <p className="text-xs text-muted-foreground font-medium">Specificaties:</p>
                        {item.components.map((comp, compIndex) => (
                          <div key={compIndex} className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">-</span>
                            <Input
                              value={comp.description || ''}
                              onChange={(e) => {
                                const newComponents = [...(item.components || [])];
                                newComponents[compIndex] = { ...newComponents[compIndex], description: e.target.value };
                                updateInstallationItem(index, 'components', newComponents);
                              }}
                              placeholder="Specificatie..."
                              className="text-xs h-7 flex-1"
                            />
                            <Input
                              type="number"
                              min="0"
                              value={comp.quantity ?? ''}
                              onChange={(e) => {
                                const newComponents = [...(item.components || [])];
                                const newQty = e.target.value === '' ? 0 : parseInt(e.target.value);
                                newComponents[compIndex] = { ...newComponents[compIndex], quantity: newQty };
                                updateInstallationItem(index, 'components', newComponents);
                              }}
                              className="text-xs h-7 w-16 text-center"
                              data-testid={`spec-qty-${index}-${compIndex}`}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-destructive"
                              onClick={() => {
                                const newComponents = item.components.filter((_, i) => i !== compIndex);
                                updateInstallationItem(index, 'components', newComponents);
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add component button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-6"
                      onClick={() => {
                        const newComponents = [...(item.components || []), { description: '', quantity: 1 }];
                        updateInstallationItem(index, 'components', newComponents);
                      }}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Specificatie toevoegen
                    </Button>
                    
                    {/* Sub-products section */}
                    {item.sub_products && item.sub_products.length > 0 && (
                      <div className="mt-3 pl-4 border-l-2 border-green-500/30 space-y-2">
                        <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                          <Package className="h-3 w-3" />
                          Sub-producten:
                        </p>
                        {item.sub_products.map((subProduct, spIndex) => (
                          <div key={spIndex} className="flex items-center gap-2 text-sm bg-green-50 dark:bg-green-950/20 p-2 rounded">
                            <span className="flex-1 text-sm">{subProduct.name}</span>
                            <span className="text-xs text-muted-foreground">
                              €{subProduct.unit_price?.toFixed(2)}/{subProduct.unit}
                            </span>
                            <Input
                              type="number"
                              min="0"
                              className="w-16 h-7 text-xs"
                              value={subProduct.quantity || 0}
                              onChange={(e) => {
                                const newSubProducts = [...(item.sub_products || [])];
                                newSubProducts[spIndex] = { 
                                  ...newSubProducts[spIndex], 
                                  quantity: parseInt(e.target.value) || 0 
                                };
                                updateInstallationItem(index, 'sub_products', newSubProducts);
                              }}
                              data-testid={`sub-product-qty-${index}-${spIndex}`}
                            />
                            <span className="text-xs text-green-600 font-medium w-20 text-right">
                              €{((subProduct.quantity || 0) * (subProduct.unit_price || 0)).toFixed(2)}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-destructive"
                              onClick={() => {
                                const newSubProducts = item.sub_products.filter((_, i) => i !== spIndex);
                                updateInstallationItem(index, 'sub_products', newSubProducts);
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {installationFormData.items.length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  Geen items toegevoegd. Klik op "Product toevoegen" om te beginnen.
                </p>
              )}
            </div>
            
            {/* Totals & BTW - Stacked on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>BTW percentage</Label>
                  <Select 
                    value={String(installationFormData.vat_percentage)} 
                    onValueChange={(v) => setInstallationFormData(prev => ({ ...prev, vat_percentage: parseInt(v) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0% (intracommunautair/vrijstelling/medecontractant)</SelectItem>
                      <SelectItem value="6">6% (woning ouder dan 10 jaar)</SelectItem>
                      <SelectItem value="21">21% (woning jonger dan 10 jaar)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Vervaldatum offerte</Label>
                  <Select 
                    value={String(installationFormData.validity_days || 8)} 
                    onValueChange={(v) => setInstallationFormData(prev => ({ ...prev, validity_days: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="installation-validity-days-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {![8, 30].includes(installationFormData.validity_days) && installationFormData.validity_days && (
                        <SelectItem value={String(installationFormData.validity_days)}>{installationFormData.validity_days} dagen (huidig)</SelectItem>
                      )}
                      <SelectItem value="8">8 dagen</SelectItem>
                      <SelectItem value="30">30 dagen</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Bepaalt de vervaldatum én de geldigheidstekst onderaan de PDF</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Totaal vermogen (Wp)</Label>
                    <Input
                      type="number"
                      data-testid="installation-total-power-wp-input"
                      value={installationFormData.total_power_wp || ''}
                      onChange={(e) => setInstallationFormData(prev => ({ ...prev, total_power_wp: e.target.value ? parseInt(e.target.value) : null }))}
                      placeholder="bijv. 6975"
                    />
                    <p className="text-xs text-muted-foreground">Automatisch berekend uit zonnepanelen × aantal</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Batterij capaciteit (kWh)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={installationFormData.total_capacity_kwh || ''}
                      onChange={(e) => setInstallationFormData(prev => ({ ...prev, total_capacity_kwh: e.target.value ? parseFloat(e.target.value) : null }))}
                      placeholder="bijv. 10.2"
                    />
                  </div>
                </div>
              </div>
              
              <Card className="p-4">
                {(() => {
                  const totals = calculateInstallationTotals();
                  return (
                    <div className="space-y-3">
                      {/* Manual subtotal toggle */}
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <Checkbox
                          id="use_manual_subtotal"
                          checked={installationFormData.use_manual_subtotal}
                          onCheckedChange={(checked) => {
                            setInstallationFormData(prev => ({
                              ...prev,
                              use_manual_subtotal: checked,
                              manual_subtotal: checked ? prev.manual_subtotal || totals.subtotal : null
                            }));
                          }}
                          data-testid="manual-subtotal-toggle"
                        />
                        <Label htmlFor="use_manual_subtotal" className="text-sm font-normal cursor-pointer">
                          Handmatige prijs gebruiken
                        </Label>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span>Subtotaal excl. BTW</span>
                        {installationFormData.use_manual_subtotal ? (
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">€</span>
                            <Input
                              type="number"
                              value={installationFormData.manual_subtotal || ''}
                              onChange={(e) => setInstallationFormData(prev => ({
                                ...prev,
                                manual_subtotal: parseFloat(e.target.value) || 0
                              }))}
                              className="w-28 h-8 text-right"
                              step="0.01"
                              min="0"
                              data-testid="manual-subtotal-input"
                            />
                          </div>
                        ) : (
                          <span>€{totals.subtotal.toFixed(2)}</span>
                        )}
                      </div>
                      {installationFormData.use_manual_subtotal && (
                        <p className="text-xs text-muted-foreground">
                          Berekende prijs: €{installationFormData.items
                            .filter(item => !item.is_optional)
                            .reduce((sum, item) => sum + (item.quantity * item.unit_price), 0)
                            .toFixed(2)}
                        </p>
                      )}
                      <div className="flex justify-between text-muted-foreground">
                        <span>BTW ({installationFormData.vat_percentage}%)</span>
                        <span>€{totals.vatAmount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold pt-2 border-t">
                        <span>Totaal incl. BTW</span>
                        <span>€{totals.totalInclVat.toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })()}
              </Card>
            </div>
            
            {/* Payment Terms & Notes - Stacked on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Betalingsvoorwaarden</Label>
                <Textarea
                  value={installationFormData.payment_terms}
                  onChange={(e) => setInstallationFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                  rows={4}
                />
              </div>
              <div className="space-y-2">
                <Label>Inbegrepen in de offerte</Label>
                <Textarea
                  value={installationFormData.included_in_offerte}
                  onChange={(e) => setInstallationFormData(prev => ({ ...prev, included_in_offerte: e.target.value }))}
                  rows={4}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Opmerkingen</Label>
              <Textarea
                value={installationFormData.notes}
                onChange={(e) => setInstallationFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                placeholder="Extra opmerkingen voor op de offerte..."
              />
            </div>
            
            {/* Custom Payment Terms per Offerte */}
            <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Factuurtermijnen (per offerte aanpasbaar)</Label>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="use_custom_terms"
                    checked={installationFormData.custom_payment_terms !== null}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        // Initialize with settings defaults or standard 40/50/10
                        const term1 = settings?.installation_term1_percentage || 40;
                        const term2 = settings?.installation_term2_percentage || 50;
                        const term3 = settings?.installation_term3_percentage || 10;
                        const newPaymentTerms = `${term1}% Voorschot\n${term2}% Na montage van de installatie en inwerkingstelling\n${term3}% Na AREI-keuring op conformiteit door een erkend organisme`;
                        setInstallationFormData(prev => ({
                          ...prev,
                          custom_payment_terms: [
                            { percentage: term1, description: 'Voorschot' },
                            { percentage: term2, description: 'Na montage' },
                            { percentage: term3, description: 'Eindfactuur' }
                          ],
                          payment_terms: newPaymentTerms
                        }));
                      } else {
                        // Reset to default payment terms
                        setInstallationFormData(prev => ({ 
                          ...prev, 
                          custom_payment_terms: null,
                          payment_terms: '40% Voorschot\n50% Na montage van de installatie en inwerkingstelling\n10% Na AREI-keuring op conformiteit door een erkend organisme'
                        }));
                      }
                    }}
                  />
                  <Label htmlFor="use_custom_terms" className="text-sm cursor-pointer">
                    Afwijkende percentages
                  </Label>
                </div>
              </div>
              
              {installationFormData.custom_payment_terms && (
                <div className="grid grid-cols-3 gap-3">
                  {installationFormData.custom_payment_terms.map((term, idx) => (
                    <div key={idx} className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        Factuur {idx + 1} - {term.description}
                      </Label>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={term.percentage}
                          onChange={(e) => {
                            const newTerms = [...installationFormData.custom_payment_terms];
                            newTerms[idx] = { ...newTerms[idx], percentage: parseFloat(e.target.value) || 0 };
                            // Update payment_terms text based on new percentages
                            const newPaymentTerms = `${newTerms[0]?.percentage || 0}% Voorschot\n${newTerms[1]?.percentage || 0}% Na montage van de installatie en inwerkingstelling\n${newTerms[2]?.percentage || 0}% Na AREI-keuring op conformiteit door een erkend organisme`;
                            setInstallationFormData(prev => ({ 
                              ...prev, 
                              custom_payment_terms: newTerms,
                              payment_terms: newPaymentTerms
                            }));
                          }}
                          className="w-20"
                        />
                        <span className="text-sm">%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {installationFormData.custom_payment_terms && (() => {
                const total = installationFormData.custom_payment_terms.reduce((sum, t) => sum + (t.percentage || 0), 0);
                return (
                  <p className={`text-xs ${total === 100 ? 'text-green-600' : 'text-red-500'}`}>
                    Totaal: {total}% {total !== 100 && '(moet 100% zijn)'}
                  </p>
                );
              })()}
            </div>
          </div>
          
          <DialogFooter className="flex-shrink-0 flex-col sm:flex-row gap-2 pt-4 border-t bg-background">
            <Button variant="outline" onClick={() => setInstallationDialogOpen(false)} className="w-full sm:w-auto">
              Annuleren
            </Button>
            <Button onClick={handleSaveInstallationOfferte} disabled={saving} className="w-full sm:w-auto">
              {saving ? 'Opslaan...' : (editingInstallationOfferte ? 'Bijwerken' : 'Offerte Aanmaken')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Installation PDF Preview Modal */}
      <Dialog open={installationPreviewOpen} onOpenChange={closeInstallationPreview}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-4 pb-2 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  {installationPreviewOfferte?.offerte_number}
                </DialogTitle>
                <DialogDescription>
                  {installationPreviewOfferte?.customer_name}
                </DialogDescription>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => installationPreviewOfferte && handleInstallationDirectDownload(installationPreviewOfferte)}
                  className="flex items-center gap-2"
                  data-testid="installation-preview-download-btn"
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden bg-muted/50">
            {installationPreviewLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-muted-foreground">PDF laden...</p>
                </div>
              </div>
            ) : installationPreviewUrl ? (
              <PdfViewer pdfUrl={installationPreviewUrl} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">Kon PDF niet laden</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Composer Modal */}
      <EmailComposer
        open={emailComposerOpen}
        onOpenChange={setEmailComposerOpen}
        type={emailComposerType}
        data={emailComposerData}
        pdfBase64={emailPdfBase64}
        pdfName={emailPdfName}
        onEmailSent={() => {
          fetchData();
          toast.success('Email verzonden en opgeslagen in Klantcontact');
        }}
      />
      
      {/* Save Template Dialog */}
      <Dialog open={saveTemplateDialogOpen} onOpenChange={setSaveTemplateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Opslaan als Template
            </DialogTitle>
            <DialogDescription>
              Sla de huidige {templateType === 'installatie' ? 'installatie' : 'reiniging'} offerte opmaak op als template voor later gebruik.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="templateName">Template naam *</Label>
              <Input
                id="templateName"
                placeholder={templateType === 'installatie' ? "Bijv. Standaard zonnepanelen installatie" : "Bijv. Standaard reiniging 20 panelen"}
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                autoFocus
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {templateType === 'installatie' 
                ? `${installationFormData.items?.length || 0} items worden opgeslagen met BTW ${installationFormData.vat_percentage}%`
                : `${formData.items?.length || 0} items worden opgeslagen met BTW ${formData.vat_percentage}%`
              }
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveTemplateDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSaveAsTemplate} disabled={savingTemplate}>
              {savingTemplate ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Opslaan...
                </>
              ) : (
                'Opslaan'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Selector Dialog - Multi-Select with Checkboxes */}
      <Dialog open={productSelectorOpen} onOpenChange={(open) => {
        setProductSelectorOpen(open);
        if (!open) {
          setSelectedProducts([]);
          setProductSearchQuery('');
          setProductCategoryFilter('all');
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Producten Toevoegen
            </DialogTitle>
            <DialogDescription>
              Selecteer één of meerdere producten om toe te voegen aan de offerte
            </DialogDescription>
          </DialogHeader>
          
          {/* Search & Category Filter */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Zoek product op naam..."
                value={productSearchQuery}
                onChange={(e) => setProductSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="product-search-input"
              />
            </div>
            <Select value={productCategoryFilter} onValueChange={setProductCategoryFilter}>
              <SelectTrigger className="w-[180px]" data-testid="product-category-filter">
                <SelectValue placeholder="Alle categorieën" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle categorieën</SelectItem>
                {productsByCategory.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Selection summary */}
          {selectedProducts.length > 0 && (
            <div className="flex items-center justify-between bg-primary/10 text-primary px-3 py-2 rounded-md">
              <span className="text-sm font-medium">
                {selectedProducts.length} product{selectedProducts.length > 1 ? 'en' : ''} geselecteerd
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSelectedProducts([])}
                className="h-7 text-xs"
              >
                Alles deselecteren
              </Button>
            </div>
          )}
          
          {/* Product Grid */}
          <div className="overflow-y-auto max-h-[50vh] pr-2">
            {productsByCategory.filter(cat => {
              // Filter by category if selected
              if (productCategoryFilter !== 'all' && cat.value !== productCategoryFilter) return false;
              if (!productSearchQuery) return cat.products.length > 0;
              return cat.products.some(p => 
                p.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                (p.description && p.description.toLowerCase().includes(productSearchQuery.toLowerCase()))
              );
            }).map(cat => {
              const filteredProducts = productSearchQuery 
                ? cat.products.filter(p => 
                    p.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                    (p.description && p.description.toLowerCase().includes(productSearchQuery.toLowerCase()))
                  )
                : cat.products;
              
              if (filteredProducts.length === 0) return null;
              
              // Check if all products in category are selected
              const allSelected = filteredProducts.every(p => selectedProducts.some(sp => sp.id === p.id));
              const someSelected = filteredProducts.some(p => selectedProducts.some(sp => sp.id === p.id));
              
              return (
                <div key={cat.value} className="mb-6">
                  <div className="flex items-center justify-between mb-3 px-1">
                    <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                      {cat.label} ({filteredProducts.length})
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => {
                        if (allSelected) {
                          // Deselect all in this category
                          setSelectedProducts(prev => 
                            prev.filter(p => !filteredProducts.some(fp => fp.id === p.id))
                          );
                        } else {
                          // Select all in this category
                          setSelectedProducts(prev => {
                            const existing = new Set(prev.map(p => p.id));
                            const toAdd = filteredProducts.filter(p => !existing.has(p.id));
                            return [...prev, ...toAdd];
                          });
                        }
                      }}
                      data-testid={`select-all-${cat.value}`}
                    >
                      {allSelected ? 'Deselecteer alles' : 'Selecteer alles'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {filteredProducts.map(product => {
                      const isSelected = selectedProducts.some(p => p.id === product.id);
                      return (
                        <div
                          key={product.id}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedProducts(prev => prev.filter(p => p.id !== product.id));
                            } else {
                              setSelectedProducts(prev => [...prev, product]);
                            }
                          }}
                          className={`p-4 border rounded-lg cursor-pointer transition-all group relative
                            ${isSelected 
                              ? 'border-primary bg-primary/10 ring-2 ring-primary/20' 
                              : 'hover:border-primary hover:bg-primary/5'
                            }`}
                          data-testid={`product-tile-${product.id}`}
                        >
                          {/* Checkbox indicator */}
                          <div className={`absolute top-2 right-2 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
                            ${isSelected 
                              ? 'bg-primary border-primary text-white' 
                              : 'border-muted-foreground/30 group-hover:border-primary/50'
                            }`}>
                            {isSelected && <CheckCircle2 className="w-3 h-3" />}
                          </div>
                          
                          <div className="font-medium text-sm group-hover:text-primary pr-6">
                            {product.name}
                          </div>
                          {/* Show Specificatie Regels (voor offerte) = components array */}
                          {product.components && product.components.length > 0 && (
                            <div className="text-xs text-muted-foreground mt-1.5 space-y-0.5">
                              {product.components.slice(0, 2).map((comp, idx) => (
                                <div key={idx}>• {comp.description}</div>
                              ))}
                              {product.components.length > 2 && (
                                <div className="text-muted-foreground/60">+{product.components.length - 2} meer...</div>
                              )}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-3 pt-2 border-t">
                            <span className="text-sm font-semibold text-primary">
                              €{product.unit_price?.toFixed(2)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              per {product.unit || 'stuk'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            
            {products.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Geen producten gevonden</p>
                <p className="text-sm">Voeg eerst producten toe in het Producten menu</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => {
              setProductSelectorOpen(false);
              setSelectedProducts([]);
              setProductSearchQuery('');
              setProductCategoryFilter('all');
            }}>
              Annuleren
            </Button>
            <Button 
              onClick={() => {
                // Add all selected products to the offerte
                selectedProducts.forEach(product => {
                  addInstallationItem(product);
                });
                toast.success(`${selectedProducts.length} product${selectedProducts.length > 1 ? 'en' : ''} toegevoegd`);
                setProductSelectorOpen(false);
                setSelectedProducts([]);
                setProductSearchQuery('');
                setProductCategoryFilter('all');
              }}
              disabled={selectedProducts.length === 0}
              data-testid="add-selected-products-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Voeg {selectedProducts.length > 0 ? `${selectedProducts.length} product${selectedProducts.length > 1 ? 'en' : ''}` : 'geselecteerde'} toe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Offerte Documenten Dialog (foto's, legplan, datasheets) */}
      <OfferteDocumenten
        open={docsDialog.open}
        onOpenChange={(open) => setDocsDialog(prev => ({ ...prev, open }))}
        offerte={docsDialog.offerte}
        entityType={docsDialog.entityType}
      />

      {/* Sharepoint projectkeuze */}
      <Dialog open={!!spProjectDialog} onOpenChange={(o) => !o && setSpProjectDialog(null)}>
        <DialogContent className="max-w-md" data-testid="sp-project-dialog">
          <DialogHeader>
            <DialogTitle>Naar welk project in Sharepoint?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Deze klant heeft al documenten in Sharepoint. Voeg de offerte toe aan een bestaand project of start een nieuw project.
          </p>
          <div className="space-y-2">
            {(spProjectDialog?.projects || []).map(p => (
              <Button key={p.number} variant="outline" className="w-full justify-between h-auto py-2.5"
                onClick={() => { const d = spProjectDialog; setSpProjectDialog(null); doSendToSharepoint(d.offerte, d.type, p.number); }}
                data-testid={`sp-project-${p.number}-btn`}>
                <span className="text-left">
                  <span className="font-semibold">Project {p.number}</span>
                  {p.quote_numbers?.length > 0 && (
                    <span className="block text-xs text-muted-foreground font-normal">Offerte {p.quote_numbers.join(', ')}</span>
                  )}
                </span>
                <span className="text-xs text-muted-foreground">{p.file_count} bestand(en)</span>
              </Button>
            ))}
            <Button className="w-full" 
              onClick={() => { const d = spProjectDialog; setSpProjectDialog(null); doSendToSharepoint(d.offerte, d.type, 'new'); }}
              data-testid="sp-new-project-btn">
              <Plus className="w-4 h-4 mr-2" /> Nieuw project
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Offertes;
